import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight, Circle, Cpu, DollarSign, LineChart, Moon, Sun, Star, ExternalLink,
  Github, Linkedin, Twitter, Leaf, Globe, Target, Shield, Lightbulb, BarChart3,
  Coins, Image as ImageIcon,
  Activity, Layers, Sparkles, Brain, MessageSquare, Bot, Wand2,
  Code2, Compass, Briefcase, Heart, Baby, UserPlus, Crown, GraduationCap,

} from 'lucide-react';

// Themed animated section backdrop. Replaces the previous broken
// /api/placeholder/* hero images. Each variant uses CSS gradients +
// SVG patterns + animated blobs tuned to that section's palette.
const SectionBackdrop = ({ theme, variant }) => {
  const palettes = {
    impact: {
      blobs: ['rgba(16,185,129,0.35)', 'rgba(132,204,22,0.30)', 'rgba(20,184,166,0.30)'],
      pattern: 'rgba(16,185,129,0.18)',
      mesh: theme === 'dark'
        ? 'radial-gradient(circle at 15% 20%, rgba(16,185,129,0.18), transparent 45%), radial-gradient(circle at 85% 70%, rgba(20,184,166,0.18), transparent 50%)'
        : 'radial-gradient(circle at 15% 20%, rgba(16,185,129,0.10), transparent 45%), radial-gradient(circle at 85% 70%, rgba(20,184,166,0.10), transparent 50%)',
    },
    crypto: {
      blobs: ['rgba(251,146,60,0.35)', 'rgba(245,158,11,0.30)', 'rgba(244,114,182,0.25)'],
      pattern: 'rgba(251,146,60,0.18)',
      mesh: theme === 'dark'
        ? 'radial-gradient(circle at 80% 25%, rgba(251,146,60,0.18), transparent 45%), radial-gradient(circle at 20% 80%, rgba(245,158,11,0.18), transparent 50%)'
        : 'radial-gradient(circle at 80% 25%, rgba(251,146,60,0.10), transparent 45%), radial-gradient(circle at 20% 80%, rgba(245,158,11,0.10), transparent 50%)',
    },
    ai: {
      blobs: ['rgba(59,130,246,0.35)', 'rgba(99,102,241,0.30)', 'rgba(14,165,233,0.30)'],
      pattern: 'rgba(99,102,241,0.20)',
      mesh: theme === 'dark'
        ? 'radial-gradient(circle at 20% 25%, rgba(59,130,246,0.18), transparent 45%), radial-gradient(circle at 85% 75%, rgba(99,102,241,0.18), transparent 50%)'
        : 'radial-gradient(circle at 20% 25%, rgba(59,130,246,0.10), transparent 45%), radial-gradient(circle at 85% 75%, rgba(99,102,241,0.10), transparent 50%)',
    },
    bazi: {
      blobs: ['rgba(168,85,247,0.35)', 'rgba(217,70,239,0.30)', 'rgba(139,92,246,0.30)'],
      pattern: 'rgba(168,85,247,0.18)',
      mesh: theme === 'dark'
        ? 'radial-gradient(circle at 75% 30%, rgba(168,85,247,0.18), transparent 45%), radial-gradient(circle at 25% 75%, rgba(217,70,239,0.18), transparent 50%)'
        : 'radial-gradient(circle at 75% 30%, rgba(168,85,247,0.10), transparent 45%), radial-gradient(circle at 25% 75%, rgba(217,70,239,0.10), transparent 50%)',
    },
  };
  const p = palettes[variant] || palettes.impact;

  // Per-variant pattern: triangle net for crypto/ai/bazi (different angles &
  // densities & color), organic dot grid for impact.
  const patterns = {
    impact: `radial-gradient(circle at 1px 1px, ${p.pattern} 1px, transparent 0)`,
    // Orange triangle net (60° / -60° / 0°)
    crypto: `repeating-linear-gradient(60deg, ${p.pattern} 0 1px, transparent 1px 22px), repeating-linear-gradient(-60deg, ${p.pattern} 0 1px, transparent 1px 22px), repeating-linear-gradient(0deg, ${p.pattern} 0 1px, transparent 1px 22px)`,
    // Blue circuit-board grid: orthogonal lines + node dots
    ai: `radial-gradient(circle at 1px 1px, ${p.pattern} 1.5px, transparent 0), repeating-linear-gradient(90deg, ${p.pattern} 0 1px, transparent 1px 36px), repeating-linear-gradient(0deg, ${p.pattern} 0 1px, transparent 1px 36px)`,
    // Purple bagua/I-Ching triangle weave (rotated 30° relative to crypto)
    bazi: `repeating-linear-gradient(30deg, ${p.pattern} 0 1px, transparent 1px 26px), repeating-linear-gradient(-30deg, ${p.pattern} 0 1px, transparent 1px 26px), repeating-linear-gradient(90deg, ${p.pattern} 0 1px, transparent 1px 26px)`,
  };
  const patternSize = {
    impact: '32px 32px',
    crypto: '44px 76px',
    ai: '36px 36px',
    bazi: '52px 90px',
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0" style={{ background: p.mesh }} />
      <div
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage: patterns[variant],
          backgroundSize: patternSize[variant],
          maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
        }}
      />
      {/* animated blobs */}
      <div
        className="absolute -top-32 -left-24 w-[28rem] h-[28rem] rounded-full filter blur-3xl opacity-50"
        style={{ background: p.blobs[0], animation: 'blobDrift 18s ease-in-out infinite alternate' }}
      />
      <div
        className="absolute -bottom-32 -right-24 w-[30rem] h-[30rem] rounded-full filter blur-3xl opacity-50"
        style={{ background: p.blobs[1], animation: 'blobDrift 22s ease-in-out infinite alternate-reverse' }}
      />
      <div
        className="absolute top-1/2 left-1/2 w-[22rem] h-[22rem] rounded-full filter blur-3xl opacity-40"
        style={{ background: p.blobs[2], animation: 'blobDrift 26s ease-in-out infinite alternate', transform: 'translate(-50%, -50%)' }}
      />
      {variant === 'bazi' && (
        <div className="absolute inset-0">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: 0.3 + Math.random() * 0.4,
                animation: `twinkle ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 2}s infinite alternate`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Counts up to a target when the element enters the viewport.
const AnimatedCounter = ({ target, duration = 1800, suffix = '' }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - t, 3);
            setVal(Math.round(target * eased));
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
};

// One of five interactive dimension cards — flips on hover/touch to reveal a
// real-world example. Independent state per card.
// Height is responsive: tall enough on narrow lg:5-col layouts where each card
// is ~200px wide and the back-side example wraps to many lines.
const DimensionFlipCard = ({ d, theme, idx }) => {
  const [flipped, setFlipped] = useState(false);
  // Detect hover-capable pointers (i.e. desktop mice). On touch devices the
  // browser still synthesises mouseenter → click → mouseleave around a tap,
  // and the combination toggles the state twice and ends up back where it
  // started, so the card "doesn't react" to taps. Wiring hover handlers
  // only on hover-capable pointers fixes that.
  const canHover = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  return (
    <div
      className="relative h-64 sm:h-60 lg:h-72 cursor-pointer select-none"
      style={{ perspective: '1000px', animation: `slideUp 0.6s ease-out ${idx * 0.08}s both` }}
      onMouseEnter={canHover ? () => setFlipped(true) : undefined}
      onMouseLeave={canHover ? () => setFlipped(false) : undefined}
      onClick={() => setFlipped((v) => !v)}
      role="button"
      tabIndex={0}
      aria-label={`${d.title} — tap to ${flipped ? 'hide' : 'see'} example`}
      aria-pressed={flipped}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFlipped((v) => !v); } }}
    >
      <div
        className="absolute inset-0 transition-transform duration-500"
        style={{ transformStyle: 'preserve-3d', transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 rounded-2xl p-5 flex flex-col"
          style={{
            backfaceVisibility: 'hidden',
            background: theme === 'dark' ? 'rgba(20,30,25,0.65)' : 'rgba(255,255,255,0.75)',
            border: `1px solid ${d.color}3d`,
            backdropFilter: 'blur(8px)',
            boxShadow: `0 10px 30px -15px ${d.color}55`,
          }}
        >
          <div className="absolute top-4 right-4 text-3xl font-bold opacity-15 pointer-events-none">{d.num}</div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
               style={{ background: `${d.color}22`, color: d.color }}>
            {d.icon}
          </div>
          <div className="font-semibold mb-1" style={{ color: d.color }}>{d.title}</div>
          <div className="text-sm opacity-75 leading-relaxed flex-1">{d.body}</div>
          <div className="text-[10px] uppercase tracking-wider opacity-50 flex items-center gap-1 mt-2">
            {canHover ? 'hover' : 'tap'} for example <ArrowRight size={10} />
          </div>
        </div>
        {/* Back — content can scroll if it still overflows on very narrow widths */}
        <div
          className="absolute inset-0 rounded-2xl p-4 flex flex-col"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            background: `linear-gradient(135deg, ${d.color}28, ${d.color}10)`,
            border: `1px solid ${d.color}88`,
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="text-[10px] uppercase tracking-widest mb-1.5 font-semibold flex items-center gap-1.5"
               style={{ color: d.color }}>
            <span className="opacity-60 font-mono">{d.num}</span>
            Real example
          </div>
          <div className="text-[13px] leading-relaxed opacity-90 flex-1 overflow-y-auto pr-1"
               style={{ scrollbarWidth: 'thin' }}>
            {d.example}
          </div>
          <div className="mt-2 pt-2 border-t text-[10px] opacity-70 italic flex items-start gap-1.5"
               style={{ borderColor: `${d.color}44` }}>
            <span className="font-mono not-italic font-bold uppercase tracking-wider opacity-70" style={{ color: d.color }}>metric</span>
            <span className="flex-1">{d.metric}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Interactive "Score Yourself" widget — 5 yes/no questions corresponding to
// the 5 dimensions; computes a live impact-credibility score.
const ScoreYourselfWidget = ({ theme }) => {
  const questions = [
    { key: 'outcome',     label: 'Is the outcome specific and measurable?',                       hint: 'e.g. "tons of CO₂e reduced," not "improved sustainability."' },
    { key: 'who',         label: 'Are the beneficiaries clearly identified?',                     hint: 'Names, locations, demographics — not "communities."' },
    { key: 'magnitude',   label: 'Is the magnitude reported (number reached, depth of change)?',  hint: 'Both reach AND depth matter.' },
    { key: 'additional',  label: 'Is additionality demonstrated (would it happen anyway?)',       hint: 'Counterfactual evidence beats correlation.' },
    { key: 'risk',        label: 'Are negative impacts and mitigations disclosed?',               hint: 'Silence on risk is a red flag, not a clean record.' },
  ];
  const [answers, setAnswers] = useState({});
  const score = Object.values(answers).filter(Boolean).length;
  const pct = Math.round((score / questions.length) * 100);
  const verdict =
    score === 5 ? { label: 'Investment-grade', color: '#10b981' } :
    score >= 4   ? { label: 'Strong evidence',  color: '#22c55e' } :
    score >= 3   ? { label: 'Promising',        color: '#fbbf24' } :
    score >= 1   ? { label: 'Thin evidence',    color: '#f97316' } :
                   { label: 'Begin assessment', color: '#94a3b8' };

  return (
    <div className="rounded-2xl p-6 mb-16"
         style={{
           background: theme === 'dark'
             ? 'linear-gradient(135deg, rgba(16,185,129,0.10), rgba(20,30,25,0.55))'
             : 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(255,255,255,0.65))',
           border: '1px solid rgba(16,185,129,0.30)',
           backdropFilter: 'blur(10px)',
         }}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-400 mb-1">
            <Sparkles size={12} /> Try it · Interactive
          </div>
          <h4 className="text-xl md:text-2xl font-bold">Score the next pitch deck you read</h4>
          <p className="text-sm opacity-70 mt-1">Five toggles, one verdict. Hover the question for a tip.</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Circular score gauge */}
          <div className="relative w-20 h-20">
            <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
              <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="6" />
              <circle cx="40" cy="40" r="34" fill="none" stroke={verdict.color} strokeWidth="6"
                      strokeDasharray={`${(pct / 100) * (2 * Math.PI * 34)} 9999`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dasharray 0.5s ease, stroke 0.3s ease' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-xl font-bold" style={{ color: verdict.color }}>{pct}</div>
              <div className="text-[8px] opacity-70 -mt-0.5">/ 100</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-60">Verdict</div>
            <div className="font-semibold transition-colors" style={{ color: verdict.color }}>{verdict.label}</div>
            <div className="text-xs opacity-60">{score} of {questions.length} dimensions met</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {questions.map((q, i) => {
          const on = !!answers[q.key];
          return (
            <button
              key={q.key}
              type="button"
              onClick={() => setAnswers((a) => ({ ...a, [q.key]: !a[q.key] }))}
              className="group flex items-start gap-3 text-left rounded-xl p-3 transition-all duration-200 hover:-translate-y-0.5"
              style={{
                background: on
                  ? 'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(16,185,129,0.05))'
                  : theme === 'dark' ? 'rgba(20,30,25,0.55)' : 'rgba(255,255,255,0.6)',
                border: `1px solid ${on ? 'rgba(16,185,129,0.55)' : 'rgba(255,255,255,0.08)'}`,
              }}
              title={q.hint}
            >
              <div
                className="mt-0.5 w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold transition-all duration-200"
                style={{
                  background: on ? '#10b981' : 'rgba(255,255,255,0.06)',
                  color: on ? '#fff' : 'transparent',
                  border: `1px solid ${on ? '#10b981' : 'rgba(255,255,255,0.18)'}`,
                }}
              >✓</div>
              <div className="flex-1">
                <div className="text-sm font-medium leading-snug">{q.label}</div>
                <div className="text-[10px] opacity-50 mt-0.5">{q.hint}</div>
              </div>
              <div className="text-[10px] uppercase tracking-wider opacity-60 self-center">{`0${i + 1}`}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Bazi Life Relationships Wheel — Day Master at center with 8 life-domain
// spokes. Each spoke maps to a "Ten Gods" (十神) relationship category that
// Bazi traditionally uses to read fortune in family / career / partner /
// children / wealth / friends / health / wisdom.
const BaziLifeWheel = ({ theme }) => {
  const center = 140;
  const radius = 96;
  // Day Master (sample): 甲 (Wood). In a real reading this would come from the chart.
  const dayMaster = { ch: '甲', en: 'Wood', color: '#4ade80' };

  const spokes = [
    { angle: -90,  label: 'Career',   ch: '事業',  god: '正官 / 七殺', accent: '#f59e0b', Icon: Briefcase,    desc: 'Status · Discipline · Opportunity' },
    { angle: -45,  label: 'Wealth',   ch: '財富',  god: '正財 / 偏財', accent: '#fbbf24', Icon: Coins,        desc: 'Income · Assets · Resources' },
    { angle: 0,    label: 'Children', ch: '子女',  god: '食神 / 傷官', accent: '#06b6d4', Icon: Baby,         desc: 'Legacy · Creativity · Joy' },
    { angle: 45,   label: 'Mentor',   ch: '貴人',  god: '正印 / 偏印', accent: '#a855f7', Icon: GraduationCap,desc: 'Wisdom · Support · Learning' },
    { angle: 90,   label: 'Health',   ch: '健康',  god: '日主強弱',    accent: '#10b981', Icon: Activity,     desc: 'Vitality · Balance · Energy' },
    { angle: 135,  label: 'Friends',  ch: '人際',  god: '比肩 / 劫財', accent: '#0ea5e9', Icon: UserPlus,     desc: 'Peers · Networks · Allies' },
    { angle: 180,  label: 'Spouse',   ch: '配偶',  god: '正官 · 正財', accent: '#ec4899', Icon: Heart,        desc: 'Partnership · Marriage · Love' },
    { angle: 225,  label: 'Family',   ch: '父母',  god: '印星 · 偏財', accent: '#8b5cf6', Icon: Crown,        desc: 'Lineage · Roots · Heritage' },
  ];

  const [active, setActive] = useState(0);
  const focus = spokes[active];

  return (
    <div className="flex-1 flex items-center justify-center min-h-0">
      <div className="relative w-full max-w-[300px]" style={{ aspectRatio: '1 / 1' }}>
        {/* SVG layer: spokes + arcs */}
        <svg viewBox="0 0 280 280" className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
          <defs>
            <radialGradient id="wheelHalo">
              <stop offset="0%" stopColor={focus.accent} stopOpacity="0.55" />
              <stop offset="60%" stopColor={focus.accent} stopOpacity="0.10" />
              <stop offset="100%" stopColor={focus.accent} stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Halo (changes color with active spoke) */}
          <circle cx={center} cy={center} r="130" fill="url(#wheelHalo)" />
          {/* Outer ring */}
          <circle cx={center} cy={center} r={radius + 16} fill="none"
                  stroke="rgba(168,85,247,0.18)" strokeWidth="0.7" strokeDasharray="2 3" />
          <circle cx={center} cy={center} r={radius - 18} fill="none"
                  stroke="rgba(168,85,247,0.14)" strokeWidth="0.5" />

          {/* Spokes */}
          {spokes.map((s, i) => {
            const ang = (s.angle * Math.PI) / 180;
            const x = center + Math.cos(ang) * (radius - 6);
            const y = center + Math.sin(ang) * (radius - 6);
            const isActive = i === active;
            return (
              <g key={s.label}>
                <line x1={center} y1={center} x2={x} y2={y}
                      stroke={isActive ? s.accent : 'rgba(168,85,247,0.22)'}
                      strokeWidth={isActive ? 1.6 : 0.8}
                      strokeDasharray={isActive ? '0' : '3 3'}
                      style={{ transition: 'stroke 0.3s, stroke-width 0.3s' }} />
                {isActive && (
                  <circle r="3" fill={s.accent}>
                    <animateMotion dur="2.4s" repeatCount="indefinite"
                                   path={`M ${center} ${center} L ${x} ${y}`} />
                    <animate attributeName="opacity" values="0;1;0" dur="2.4s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            );
          })}
        </svg>

        {/* Center: Day Master */}
        <div
          className="absolute rounded-full flex flex-col items-center justify-center text-center"
          style={{
            left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
            width: 78, height: 78,
            background: theme === 'dark'
              ? 'radial-gradient(circle, rgba(40,30,55,0.95), rgba(20,15,35,0.85))'
              : 'radial-gradient(circle, rgba(255,255,255,0.95), rgba(243,232,255,0.85))',
            border: `1.5px solid ${dayMaster.color}aa`,
            boxShadow: `0 0 24px ${dayMaster.color}66, inset 0 0 12px ${dayMaster.color}33`,
            zIndex: 5,
          }}
          title={`Day Master 日主 · ${dayMaster.en}`}
        >
          <div className="text-[8px] uppercase tracking-widest opacity-70">Day Master</div>
          <div className="text-3xl font-bold leading-none mt-0.5" style={{ color: dayMaster.color, textShadow: `0 0 12px ${dayMaster.color}88` }}>
            {dayMaster.ch}
          </div>
          <div className="text-[9px] opacity-60 mt-0.5">日主 · {dayMaster.en}</div>
        </div>

        {/* Spoke chips (HTML, positioned in polar) */}
        {spokes.map((s, i) => {
          const ang = (s.angle * Math.PI) / 180;
          const x = 50 + (Math.cos(ang) * radius * 100) / 280;
          const y = 50 + (Math.sin(ang) * radius * 100) / 280;
          const isActive = i === active;
          return (
            <button
              key={s.label}
              type="button"
              onClick={() => setActive(i)}
              onMouseEnter={() => setActive(i)}
              className="absolute flex flex-col items-center justify-center rounded-full transition-all duration-300 group"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: `translate(-50%, -50%) scale(${isActive ? 1.08 : 1})`,
                width: 56,
                height: 56,
                background: theme === 'dark'
                  ? `linear-gradient(135deg, ${s.accent}28, rgba(20,15,35,0.85))`
                  : `linear-gradient(135deg, ${s.accent}22, rgba(255,255,255,0.85))`,
                border: `1.5px solid ${s.accent}${isActive ? 'cc' : '55'}`,
                boxShadow: isActive
                  ? `0 0 20px ${s.accent}88, 0 0 0 3px ${s.accent}22`
                  : `0 4px 12px rgba(0,0,0,0.18)`,
                zIndex: 4,
                cursor: 'pointer',
                color: s.accent,
              }}
              title={`${s.label} · ${s.ch}`}
            >
              <s.Icon size={16} />
              <span className="text-[10px] font-bold mt-0.5" style={{ fontFamily: 'serif' }}>{s.ch}</span>
            </button>
          );
        })}

        {/* Center info card below the wheel - shows active spoke */}
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-xl px-3 py-2 backdrop-blur-sm transition-all duration-300"
          style={{
            top: 'calc(100% + 4px)',
            background: theme === 'dark' ? 'rgba(30,20,45,0.7)' : 'rgba(255,255,255,0.85)',
            border: `1px solid ${focus.accent}55`,
            minWidth: 200,
            maxWidth: '100%',
          }}
        >
          <div className="flex items-center justify-center gap-2">
            <span className="text-base font-bold" style={{ color: focus.accent, fontFamily: 'serif' }}>{focus.ch}</span>
            <span className="text-xs font-semibold opacity-80">{focus.label}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                  style={{ background: `${focus.accent}22`, color: focus.accent }}>
              {focus.god}
            </span>
          </div>
          <div className="text-[10px] opacity-60 text-center mt-0.5">{focus.desc}</div>
        </div>
      </div>
    </div>
  );
};

// Live "current hour" earthly branch indicator for the Bazi card.
// Each Chinese double-hour (時辰) maps to one of the 12 earthly branches.
const BaziHourBadge = ({ theme }) => {
  const branches = [
    { ch: '子', en: 'Zǐ',   range: '23–01', element: 'Water' },
    { ch: '丑', en: 'Chǒu', range: '01–03', element: 'Earth' },
    { ch: '寅', en: 'Yín',  range: '03–05', element: 'Wood'  },
    { ch: '卯', en: 'Mǎo',  range: '05–07', element: 'Wood'  },
    { ch: '辰', en: 'Chén', range: '07–09', element: 'Earth' },
    { ch: '巳', en: 'Sì',   range: '09–11', element: 'Fire'  },
    { ch: '午', en: 'Wǔ',   range: '11–13', element: 'Fire'  },
    { ch: '未', en: 'Wèi',  range: '13–15', element: 'Earth' },
    { ch: '申', en: 'Shēn', range: '15–17', element: 'Metal' },
    { ch: '酉', en: 'Yǒu',  range: '17–19', element: 'Metal' },
    { ch: '戌', en: 'Xū',   range: '19–21', element: 'Earth' },
    { ch: '亥', en: 'Hài',  range: '21–23', element: 'Water' },
  ];
  const elementColors = {
    Wood:  '#4ade80',
    Fire:  '#f87171',
    Earth: '#8B4513',
    Metal: '#FFD700',
    Water: '#60a5fa',
  };
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  // 23:00–00:59 → Zǐ (index 0); else floor((h+1)/2)
  const h = now.getHours();
  const idx = h === 23 ? 0 : Math.floor((h + 1) / 2);
  const b = branches[idx];
  const color = elementColors[b.element];
  return (
    <div
      className="flex items-center gap-1.5 rounded-full px-2.5 py-1"
      style={{
        background: theme === 'dark' ? 'rgba(30,20,45,0.55)' : 'rgba(255,255,255,0.6)',
        border: `1px solid ${color}55`,
      }}
      title={`Current 時辰: ${b.ch} ${b.en} · ${b.range} · ${b.element}`}
    >
      <span className="text-[10px] uppercase tracking-wider opacity-60">Now</span>
      <span className="text-base font-semibold leading-none" style={{ color, textShadow: `0 0 8px ${color}66` }}>{b.ch}</span>
      <span className="text-[10px] opacity-70 font-mono">{b.range}</span>
    </div>
  );
};

const App = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState('hero');
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  // Theme: dark by default. Only respect the saved preference if the user
  // has explicitly toggled before — system colour-scheme is intentionally
  // ignored so the brand-default (dark) shows for first-time visitors.
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    try {
      const saved = window.localStorage.getItem('tw-theme');
      if (saved === 'dark' || saved === 'light') return saved;
    } catch (e) {}
    return 'dark';
  });
  const [mouseEntered, setMouseEntered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [titleVisible, setTitleVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef(null);
  const keywordsContainerRef = useRef(null);

  // Persist theme + enable smooth scroll for in-page anchor jumps.
  useEffect(() => {
    try { window.localStorage.setItem('tw-theme', theme); } catch (e) {}
  }, [theme]);
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => { document.documentElement.style.scrollBehavior = ''; };
  }, []);
  // Close mobile menu on Escape
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen]);

  // Set loaded state after component mounts for entrance animations
  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 300);
  }, []);

  // Then update the useEffect hook to trigger the title visibility
  useEffect(() => {
    if (isLoaded) {
      // Set title visible after the page has loaded
      setTimeout(() => {
        setTitleVisible(true);
      }, 300);
    }
  }, [isLoaded]);

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Determine active section based on scroll position
      const sections = ['hero', 'impact', 'ai', 'bazi', 'crypto'];
      const currentSection = sections.find((section, index) => {
        const element = document.getElementById(section);
        if (!element) return false;
        const top = element.offsetTop;
        const height = element.offsetHeight;
        return scrollY >= top - 300 && scrollY < top + height - 300;
      });
      
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollY]);

  // Track cursor for custom effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Toggle theme
  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // 3D tilt effect for cards
  const handleMouseMove3D = (e, intensity = 20) => {
    if (!e.currentTarget) return;
    
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / centerY * -intensity;
    const rotateY = (x - centerX) / centerX * intensity;
    
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };
  
  const resetCard = (e) => {
    if (!e.currentTarget) return;
    e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
  };

  const getElementInfo = (char) => {
    const elements = {
      Wood: { color: '#4ade80', stems: ['甲', '乙'], branches: ['寅', '卯'] },
      Fire: { color: '#f87171', stems: ['丙', '丁'], branches: ['巳', '午'] },
      Earth: { color: '#8B4513', stems: ['戊', '己'], branches: ['辰', '戌', '丑', '未'] },
      Metal: { color: '#FFD700', stems: ['庚', '辛'], branches: ['申', '酉'] }, // Changed from #d1d5db to #FFD700 (gold)
      Water: { color: '#60a5fa', stems: ['壬', '癸'], branches: ['亥', '子'] },
    };
    for (const [name, data] of Object.entries(elements)) {
      if (data.stems.includes(char) || data.branches.includes(char)) {
        return { name, color: data.color };
      }
    }
    return { name: 'Unknown', color: '#9ca3af' }; // Default
  };

  const handleMouseMoveKeywords = (e) => {
    if (!keywordsContainerRef.current) return;
    
    const keywords = keywordsContainerRef.current.querySelectorAll('.keyword-item');
    const container = keywordsContainerRef.current.getBoundingClientRect();
    
    // Calculate mouse position relative to container center
    const centerX = container.width / 2;
    const centerY = container.height / 2;
    const mouseX = e.clientX - container.left - centerX;
    const mouseY = e.clientY - container.top - centerY;
    
    // Move each keyword based on mouse position
    keywords.forEach((keyword, index) => {
      const factor = (index % 5 + 1) * 0.01; // Different movement factors for variety
      const x = mouseX * factor;
      const y = mouseY * factor;
      keyword.style.transform = `translate(${x}px, ${y}px) scale(${0.8 + Math.random() * 0.7})`;
    });
  };

  // Add this useEffect hook after other useEffect hooks in the component
  useEffect(() => {
    // Function to fetch crypto prices from CoinGecko API
    const fetchCryptoPrices = async () => {
      try {
        // CoinGecko Demo tier accepts the API key either as the
        // `x-cg-demo-api-key` header OR as a `x_cg_demo_api_key` query
        // parameter. The header form triggers a CORS preflight which the
        // free tier sometimes does not answer correctly from a browser →
        // we get a network error and fall through to the mock prices.
        // The query-param form is a "simple" GET with no preflight.
        const COINGECKO_DEMO_KEY = 'CG-46uVpv1n8pWG25fVizihjDmp';
        const response = await fetch(
          'https://api.coingecko.com/api/v3/coins/markets'
          + '?vs_currency=usd'
          + '&ids=bitcoin,ethereum,binancecoin,solana,cardano,avalanche-2,polkadot,tron,matic-network,dogecoin,shiba-inu'
          + '&order=market_cap_desc&per_page=20&page=1&sparkline=false&price_change_percentage=24h'
          + `&x_cg_demo_api_key=${COINGECKO_DEMO_KEY}`,
          { headers: { 'Accept': 'application/json' } }
        );
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        
        const data = await response.json();
        updatePriceTicker(data);
      } catch (error) {
        console.error('Error fetching crypto prices:', error);
        // Use fallback data if API fails
        updatePriceTicker(null);
      }
    };

    // Function to update the price ticker with live data
    const updatePriceTicker = (data) => {
      const ticker = document.getElementById('crypto-price-ticker');
      if (!ticker) return;
      
      // Clear current content
      ticker.innerHTML = '';
      
      // Sample fallback data in case API fails - formatted like the markets endpoint
      const fallbackData = [
        { id: 'bitcoin', symbol: 'btc', current_price: 45230, price_change_percentage_24h: 2.4 },
        { id: 'ethereum', symbol: 'eth', current_price: 2450, price_change_percentage_24h: -1.2 },
        { id: 'binancecoin', symbol: 'bnb', current_price: 320, price_change_percentage_24h: 1.8 },
        { id: 'solana', symbol: 'sol', current_price: 98, price_change_percentage_24h: -2.3 },
        { id: 'cardano', symbol: 'ada', current_price: 0.35, price_change_percentage_24h: -0.8 },
        { id: 'avalanche-2', symbol: 'avax', current_price: 22, price_change_percentage_24h: 3.5 },
        { id: 'polkadot', symbol: 'dot', current_price: 5.2, price_change_percentage_24h: 1.5 },
        { id: 'tron', symbol: 'trx', current_price: 0.11, price_change_percentage_24h: 0.4 },
        { id: 'matic-network', symbol: 'matic', current_price: 0.6, price_change_percentage_24h: 2.1 },
        { id: 'dogecoin', symbol: 'doge', current_price: 0.08, price_change_percentage_24h: 5.2 },
        { id: 'shiba-inu', symbol: 'shib', current_price: 0.000008, price_change_percentage_24h: -3.1 }
      ];
      
      // Use API data or fallback. Filter out coins missing a price — some
      // CoinGecko IDs (e.g. retired tokens like matic-network) can come back
      // with current_price === null, which crashes the renderer.
      const rawData = data || fallbackData;
      const priceData = (Array.isArray(rawData) ? rawData : [])
        .filter((c) => c && typeof c.current_price === 'number' && !Number.isNaN(c.current_price));
      if (priceData.length === 0) {
        // Fall back if every entry was nullified
        priceData.push(...fallbackData);
      }

      // Populate the Top 3 highlight tiles (BTC, ETH, SOL)
      const topPanel = document.getElementById('crypto-top-coins');
      if (topPanel) {
        const wantedIds = ['bitcoin', 'ethereum', 'solana'];
        const top = wantedIds
          .map((id) => priceData.find((c) => c.id === id))
          .filter(Boolean);
        topPanel.innerHTML = '';
        top.forEach((coin) => {
          const change = coin.price_change_percentage_24h ?? 0;
          const isUp = change >= 0;
          const price = coin.current_price;
          const priceFmt = price >= 1000
            ? '$' + price.toLocaleString(undefined, { maximumFractionDigits: 0 })
            : price >= 1
              ? '$' + price.toFixed(2)
              : '$' + price.toFixed(4);
          const tile = document.createElement('div');
          tile.className = 'rounded-xl px-3 py-2 border';
          tile.style.background = 'linear-gradient(135deg, rgba(40,28,18,0.65), rgba(20,16,12,0.45))';
          tile.style.borderColor = isUp ? 'rgba(34,197,94,0.30)' : 'rgba(239,68,68,0.30)';
          tile.innerHTML = `
            <div class="flex items-center justify-between">
              <div class="text-[10px] uppercase tracking-wider opacity-70 font-semibold">${coin.symbol.toUpperCase()}</div>
              <div class="text-[9px] px-1.5 py-0.5 rounded-full font-mono" style="background:${isUp ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'};color:${isUp ? '#22c55e' : '#ef4444'}">${isUp ? '▲' : '▼'} ${Math.abs(change).toFixed(2)}%</div>
            </div>
            <div class="mt-0.5 text-base font-bold tracking-tight">${priceFmt}</div>
          `;
          topPanel.appendChild(tile);
        });
      }

      const cryptoData = [...priceData].sort((a, b) => {
        // If IDs match predefined order, use that order
        const ids = ['bitcoin', 'ethereum', 'binancecoin', 'solana', 'cardano', 'avalanche-2', 'polkadot', 'tron', 'matic-network', 'dogecoin', 'shiba-inu'];
        const aIndex = ids.indexOf(a.id);
        const bIndex = ids.indexOf(b.id);
        if (aIndex >= 0 && bIndex >= 0) {
          return aIndex - bIndex;
        }
        // Otherwise, sort by price (as a fallback)
        return b.current_price - a.current_price;
      });
      
      // Create display elements
      const createPriceElements = (ticker) => {
        cryptoData.forEach((coin, i) => {
          // Format the price
          const price = coin.current_price;
          const priceFormatted = price < 0.01 ? price.toFixed(6) : price.toFixed(2);
          
          // Get the percentage change - reliable from the markets endpoint
          let change = 0;
          if (coin.price_change_percentage_24h !== undefined && coin.price_change_percentage_24h !== null) {
            change = parseFloat(coin.price_change_percentage_24h).toFixed(1);
          }
          
          const isPositive = parseFloat(change) >= 0;
          
          const item = document.createElement('div');
          item.className = 'flex items-center gap-1.5 px-3 border-r border-amber-500/15 last:border-r-0';
          item.innerHTML = `
            <span class="text-[10px] font-bold text-amber-500/90 tracking-wider">${coin.symbol.toUpperCase()}</span>
            <span class="text-[10px] font-mono">${'$' + priceFormatted}</span>
            <span class="text-[10px] font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}">${isPositive ? '▲' : '▼'} ${Math.abs(parseFloat(change)).toFixed(1)}%</span>
          `;
          ticker.appendChild(item);
        });
        
        // Clone elements for continuous scroll
        cryptoData.forEach((coin, i) => {
          // Format the price
          const price = coin.current_price;
          const priceFormatted = price < 0.01 ? price.toFixed(6) : price.toFixed(2);
          
          // Get the percentage change
          let change = 0;
          if (coin.price_change_percentage_24h !== undefined && coin.price_change_percentage_24h !== null) {
            change = parseFloat(coin.price_change_percentage_24h).toFixed(1);
          }
          
          const isPositive = parseFloat(change) >= 0;
          
          const item = document.createElement('div');
          item.className = 'flex items-center gap-1.5 px-3 border-r border-amber-500/15 last:border-r-0';
          item.innerHTML = `
            <span class="text-[10px] font-bold text-amber-500/90 tracking-wider">${coin.symbol.toUpperCase()}</span>
            <span class="text-[10px] font-mono">${'$' + priceFormatted}</span>
            <span class="text-[10px] font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}">${isPositive ? '▲' : '▼'} ${Math.abs(parseFloat(change)).toFixed(1)}%</span>
          `;
          ticker.appendChild(item);
        });
      };
      
      createPriceElements(ticker);
    };
    
    // Initial fetch
    fetchCryptoPrices();

    // Set interval to update prices every 60 seconds
    const intervalId = setInterval(fetchCryptoPrices, 60000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array means this runs once when component mounts

  // Per-chain block counters with realistic cadences:
  // BTC ~10 min, ETH ~12 s, SOL ~400 ms.
  useEffect(() => {
    let btc = 872341, eth = 21082442, sol = 312847902;
    const writeAll = (cls, val) => {
      document.querySelectorAll(cls).forEach((el) => { el.textContent = val.toLocaleString(); });
    };
    writeAll('.block-counter-btc', btc);
    writeAll('.block-counter-eth', eth);
    writeAll('.block-counter-sol', sol);
    // SOL slot tick — every 400 ms
    const idSol = setInterval(() => { sol += 1; writeAll('.block-counter-sol', sol); }, 400);
    // ETH block tick — every 12 s
    const idEth = setInterval(() => { eth += 1; writeAll('.block-counter-eth', eth); }, 12000);
    // BTC block tick — every 60 s (compressed for demo legibility)
    const idBtc = setInterval(() => { btc += 1; writeAll('.block-counter-btc', btc); }, 60000);
    return () => { clearInterval(idSol); clearInterval(idEth); clearInterval(idBtc); };
  }, []);

  // Typing animation for the AI Explorer card prompt/response
  useEffect(() => {
    const conversations = [
      { p: 'Summarise this 80-page sustainability report in 3 takeaways…', r: 'Done. Top risks: scope-3 visibility, water stress in two facilities, and disclosure gaps vs. CSRD.' },
      { p: 'Spin up an MCP tool that pulls Bazi data for a date…', r: 'Tool registered. Calling: bazi.getChart(birthdate, time, location).' },
      { p: 'Generate a hero image for an impact investing essay…', r: 'Rendering: cinematic light over wind turbines, 16:9, soft teal palette.' },
      { p: 'Score this pitch deck against IRIS+ metrics…', r: 'Aligned: PI4060, OI6376. Missing additionality evidence in slide 7.' },
    ];
    let convoIdx = 0;
    let cancelled = false;

    const typeInto = async (selector, text, speed) => {
      const el = document.querySelector(selector);
      if (!el) return;
      el.textContent = '';
      for (let i = 0; i < text.length && !cancelled; i++) {
        el.textContent += text[i];
        await new Promise((r) => setTimeout(r, speed));
      }
    };
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      while (!cancelled) {
        const c = conversations[convoIdx % conversations.length];
        await typeInto('.ai-typed-prompt', c.p, 28);
        await wait(450);
        await typeInto('.ai-typed-response', c.r, 22);
        await wait(2400);
        convoIdx++;
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Add touch-friendly interactions for mobile
  const handleTouchStart = (e) => {
    // Store initial touch position
    if (!e.currentTarget) return;
    const touch = e.touches[0];
    e.currentTarget._touchX = touch.clientX;
    e.currentTarget._touchY = touch.clientY;
  };

  const handleTouchMove = (e) => {
    // Create 3D effect for touch devices
    if (!e.currentTarget || !e.currentTarget._touchX) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - e.currentTarget._touchX;
    const deltaY = touch.clientY - e.currentTarget._touchY;
    
    const rotateY = deltaX * 0.2;
    const rotateX = -deltaY * 0.2;
    
    e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  return (
    <div className={`min-h-screen w-full ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'} transition-colors duration-500 overflow-hidden`}>
      {/* Dynamic cursor — hidden on touch / small screens (md = 768 px) where
          the cursor never moves and the static dot looks like a stray UI bug */}
      <div 
        className="hidden md:block fixed w-8 h-8 rounded-full pointer-events-none z-50 mix-blend-difference transition-transform duration-200 ease-out"
        style={{
          background: 'radial-gradient(circle, rgba(99,102,241,0.8) 0%, rgba(99,102,241,0) 70%)',
          transform: `translate(${cursorPosition.x - 16}px, ${cursorPosition.y - 16}px) scale(${mouseEntered ? 3 : 1})`,
          filter: 'blur(4px)',
        }}
      />
      <div 
        className="hidden md:block fixed w-4 h-4 bg-white rounded-full pointer-events-none z-50 transition-transform duration-100 ease-out"
        style={{
          transform: `translate(${cursorPosition.x - 2}px, ${cursorPosition.y - 2}px)`,
          opacity: 0.8,
        }}
      />

      {/* Futuristic grid background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className={`w-full h-full ${theme === 'dark' ? 'opacity-20' : 'opacity-5'}`} 
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(99,102,241,0.1) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(99,102,241,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            transform: `translateY(${scrollY * 0.1}px)`,
          }}
        />
      </div>

      {/* Floating particles effect */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 15 + 2}px`,
              height: `${Math.random() * 15 + 2}px`,
              background: i % 5 === 0 ? '#61dafb' : i % 5 === 1 ? '#f7931a' : i % 5 === 2 ? '#4caf50' : i % 5 === 3 ? '#e91e63' : '#9c27b0',
              transform: `translate(${Math.sin((scrollY + i * 100) / 1000) * 150}px, ${Math.cos((scrollY + i * 100) / 1000) * 150}px)`,
              filter: 'blur(1px)',
              transition: 'transform 0.5s ease-out',
              boxShadow: `0 0 20px 2px ${i % 5 === 0 ? 'rgba(97, 218, 251, 0.3)' : i % 5 === 1 ? 'rgba(247, 147, 26, 0.3)' : i % 5 === 2 ? 'rgba(76, 175, 80, 0.3)' : i % 5 === 3 ? 'rgba(233, 30, 99, 0.3)' : 'rgba(156, 39, 176, 0.3)'}`,
            }}
          />
        ))}
      </div>
      
      {/* Radial gradient overlays for depth */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-70"
           style={{
             background: theme === 'dark' 
               ? 'radial-gradient(circle at 50% 50%, rgba(20, 20, 30, 0) 0%, rgba(20, 20, 30, 0.8) 100%)' 
               : 'radial-gradient(circle at 50% 50%, rgba(235, 235, 245, 0) 0%, rgba(235, 235, 245, 0.8) 100%)'
           }}
      />

      {/* Theme toggle button */}
      <button 
        onClick={toggleTheme}
        onMouseEnter={() => setMouseEntered(true)}
        onMouseLeave={() => setMouseEntered(false)}
        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        className="fixed top-6 right-6 z-50 p-3 rounded-full bg-opacity-20 backdrop-blur-md transition-all duration-500 hover:rotate-90 hover:scale-110"
        style={{
          background: theme === 'dark' ? 'rgba(30, 30, 40, 0.4)' : 'rgba(240, 240, 250, 0.4)',
          boxShadow: '0 0 20px rgba(99, 102, 241, 0.3)',
        }}
      >
        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      {/* Floating "Get in touch" CTA — mailto with LinkedIn fallback in title */}
      <a
        href="https://www.linkedin.com/in/josephl1/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get in touch on LinkedIn"
        className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-full font-semibold text-sm flex items-center gap-2 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
        style={{
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #ec4899)',
          color: 'white',
          boxShadow: '0 10px 30px -8px rgba(139, 92, 246, 0.6), 0 0 0 1px rgba(255,255,255,0.1) inset',
        }}
      >
        <Linkedin size={16} />
        <span className="hidden sm:inline">Get in touch</span>
      </a>

      {/* Navigation */}
      <nav 
        className={`fixed top-0 w-full z-40 transition-all duration-500 ${
          scrollY > 50 ? 'py-3 backdrop-blur-md' : 'py-6'
        } ${theme === 'dark' ? 'bg-gray-900/80' : 'bg-white/80'}`}
        style={{
          transform: `translateY(${isLoaded ? '0' : '-100px'})`,
          boxShadow: scrollY > 50 ? '0 5px 30px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          <div className="text-xl font-bold relative overflow-visible">
            <a 
              href="http://linkedin.com/in/josephl1/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <Linkedin size={24} className="text-indigo-500" />
              <span className="bg-gradient-to-r from-purple-400 via-indigo-500 to-violet-600 text-transparent bg-clip-text whitespace-nowrap">
                LinkedIn
              </span>
            </a>
          </div>
          <div className="hidden md:flex space-x-10 items-center">
            {['Impact', 'AI', 'Bazi', 'Crypto'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                onMouseEnter={() => setMouseEntered(true)}
                onMouseLeave={() => setMouseEntered(false)}
                className={`transition-all duration-300 relative hover:tracking-wider ${
                  activeSection === item.toLowerCase() 
                    ? 'font-medium text-indigo-500' 
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                {item}
                <span 
                  className={`absolute -bottom-1 left-0 h-0.5 bg-indigo-500 transition-all duration-500 rounded-full ${
                    activeSection === item.toLowerCase() ? 'w-full' : 'w-0'
                  }`} 
                />
              </a>
            ))}
            {/* Live HK time + 時辰 (desktop) */}
            <BaziHourBadge theme={theme} />
          </div>
          <button
            className="md:hidden p-2 -mr-2 rounded-lg transition-colors hover:bg-white/10"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            <div className="w-6 h-6 relative flex items-center justify-center" aria-hidden="true">
              <span className={`absolute h-0.5 w-6 bg-current transition-transform duration-300 ${mobileMenuOpen ? 'rotate-45' : '-translate-y-2'}`} />
              <span className={`absolute h-0.5 w-6 bg-current transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
              <span className={`absolute h-0.5 w-6 bg-current transition-transform duration-300 ${mobileMenuOpen ? '-rotate-45' : 'translate-y-2'}`} />
            </div>
          </button>
        </div>
        {/* Mobile drawer */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-96 mt-4' : 'max-h-0 mt-0'}`}
          style={{ borderTop: mobileMenuOpen ? `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` : 'none' }}
        >
          <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
            {['Impact', 'AI', 'Bazi', 'Crypto'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-3 px-3 rounded-lg transition-colors ${
                  activeSection === item.toLowerCase()
                    ? 'bg-indigo-500/15 text-indigo-400 font-medium'
                    : 'opacity-80 hover:bg-white/5'
                }`}
              >
                {item}
              </a>
            ))}
            <div className="pt-2 mt-2 flex items-center justify-between" style={{ borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
              <BaziHourBadge theme={theme} />
              <a
                href="https://www.linkedin.com/in/josephl1/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' }}
              >
                <Linkedin size={14} /> Connect
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* Replace static image with animated keywords background */}
          <div 
            ref={keywordsContainerRef}
            className="absolute inset-0 w-full h-full overflow-hidden"
            onMouseMove={handleMouseMoveKeywords}
          >
            <div className="keywords-container">
              {[
                'Innovation', 'Sustainability', 'Impact', 'Technology', 'Future',
                'Growth', 'Transformation', 'Development', 'Progress', 'AI',
                'Clean Energy', 'Climate Action', 'Empowerment', 'Blockchain',
                'Digital', 'Equality', 'Global Goals', 'Community', 'Solutions',
                'Renewable', 'Smart Cities', 'ESG', 'Circular Economy', 'Net Zero'
              ].map((word, index) => (
                <div 
                  key={index}
                  className="keyword-item"
                  style={{
                    left: `${5 + (index % 8) * 12}%`, // Better distribution horizontally
                    top: `${10 + Math.floor(index / 8) * 20}%`, // Better distribution vertically
                    animationDelay: `${index * 0.3}s`,
                    opacity: 0.1 + (Math.random() * 0.3),
                    color: index % 5 === 0 ? '#818cf8' : 
                           index % 5 === 1 ? '#a78bfa' : 
                           index % 5 === 2 ? '#f472b6' : 
                           index % 5 === 3 ? '#60a5fa' : '#4ade80', // Added more colors
                  }}
                >
                  {word}
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-900" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-full max-w-3xl mx-auto">
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-9xl font-bold mb-6 tracking-tight transition-all duration-1000 ease-out"
                   style={{
                     transform: `translateY(${isLoaded ? '0' : '50px'})`,
                     opacity: isLoaded ? 1 : 0,
                   }}>
                {/* Title content */}
                <div className="overflow-hidden relative">
                  <span className="inline-block transition-transform duration-500" 
                        style={{ transform: `translateY(${titleVisible ? '0' : '100%'})` }}>
                    Hello, I'm
                  </span>
                </div>
                <div className="overflow-hidden relative mt-2 pb-4" style={{ lineHeight: "1.2" }}>
                  <span className={`inline-block transition-transform duration-500 delay-300 bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 text-transparent bg-clip-text`}
                        style={{ 
                          transform: `translateY(${titleVisible ? '0' : '100%'})`,
                          paddingBottom: "8px" // Add padding to the text itself
                        }}>
                    Joseph Leung
                  </span>
                </div>
              </h1>
              
              <div className="mb-6 text-base sm:text-lg md:text-xl opacity-80 leading-relaxed px-2"
                   style={{
                     transform: `translateY(${isLoaded ? '0' : '30px'})`,
                     opacity: isLoaded ? 1 : 0,
                     transition: 'transform 1s ease-out 0.3s, opacity 1s ease-out 0.3s',
                   }}>
                Impact-investing & climate-tech analyst — exploring where{' '}
                <span className="font-semibold text-emerald-400">capital</span>,{' '}
                <span className="font-semibold text-blue-400">technology</span> and{' '}
                <span className="font-semibold text-purple-400">ancient wisdom</span> meet.
              </div>
              <div className="mb-12 flex flex-wrap items-center justify-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.18em] opacity-70"
                   style={{
                     transform: `translateY(${isLoaded ? '0' : '30px'})`,
                     opacity: isLoaded ? 0.7 : 0,
                     transition: 'transform 1s ease-out 0.5s, opacity 1s ease-out 0.5s',
                   }}>
                {['CFA', 'CAIA', 'CESGA', 'FCPA'].map((c) => (
                  <span key={c} className="px-2.5 py-1 rounded-full"
                        style={{
                          background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                          border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)'}`,
                        }}>
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div 
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 filter blur-3xl" 
            style={{ 
              background: 'linear-gradient(to right, #6366f1, #8b5cf6)',
              transform: `translate(${-scrollY * 0.15}px, ${scrollY * 0.05}px)`,
              animation: 'pulse 8s infinite alternate',
            }} 
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-20 filter blur-3xl" 
            style={{ 
              background: 'linear-gradient(to right, #ec4899, #8b5cf6)',
              transform: `translate(${scrollY * 0.12}px, ${-scrollY * 0.05}px)`,
              animation: 'pulse 12s infinite alternate-reverse',
            }} 
          />
        </div>
      </section>

      {/* Impact Investing Section */}
      <section
        id="impact"
        className={`min-h-screen relative py-24 ${theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-200/50'} backdrop-blur-md`}
      >
        <SectionBackdrop theme={theme} variant="impact" />
        <div className={`absolute inset-0 bg-gradient-to-b ${theme === 'dark' ? 'from-gray-900/60 via-transparent to-gray-900/60' : 'from-gray-100/60 via-transparent to-gray-100/60'}`} />

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 relative">
              <div className={`absolute -top-10 -left-10 text-9xl opacity-5 font-bold ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>01</div>
              <div 
                className="overflow-hidden mb-4"
                style={{
                  transform: `translateY(${Math.max(0, 100 - Math.max(0, scrollY - 300) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 300) / 200),
                  transition: 'transform 0.8s ease-out, opacity 0.8s ease-out',
                }}
              >
                <h2 className="text-4xl md:text-6xl font-bold mb-2 relative bg-gradient-to-r from-green-400 to-emerald-600 text-transparent bg-clip-text">
                  Impact Investing
                </h2>
                <div className="h-1 w-24 bg-gradient-to-r from-green-500 to-emerald-700 rounded-full" />
              </div>
              
              <div 
                className="text-lg mb-6 opacity-80 max-w-xl"
                style={{
                  transform: `translateY(${Math.max(0, 120 - Math.max(0, scrollY - 300) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 300) / 250),
                  transition: 'transform 1s ease-out, opacity 1s ease-out',
                }}
              >
                <p>Catalyzing positive change through strategic investments that deliver both financial returns and measurable social or environmental impact. My portfolio focuses on climate technologies, healthcare innovation, financial inclusion initiatives, educational advancement, and equitable access to essential resources.</p>
              </div>
              
              <div 
                className="flex flex-wrap gap-3 mb-8"
                style={{
                  transform: `translateY(${Math.max(0, 140 - Math.max(0, scrollY - 300) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 300) / 300),
                  transition: 'transform 1.2s ease-out, opacity 1.2s ease-out',
                }}
              >
                {['Sustainability', 'Impact Investing', 'ESG', 'Climate Tech', 'Social Innovation'].map((tag, i) => (
                  <span 
                    key={tag} 
                    className={`px-4 py-1 rounded-full text-sm relative overflow-hidden group`}
                    style={{
                      transition: 'all 0.3s ease-out',
                      transitionDelay: `${i * 100}ms`,
                    }}
                  >
                    <span className={`absolute inset-0 ${theme === 'dark' ? 'bg-green-900/60' : 'bg-green-100'} group-hover:scale-110 transition-transform duration-300`}></span>
                    <span className="relative z-10">{tag}</span>
                  </span>
                ))}
              </div>
              
              <div 
                className="flex items-center gap-6"
                style={{
                  transform: `translateY(${Math.max(0, 160 - Math.max(0, scrollY - 300) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 300) / 350),
                  transition: 'transform 1.4s ease-out, opacity 1.4s ease-out',
                }}
              >
                <div className="flex items-center gap-2">
                  <LineChart size={20} className="text-green-500" />
                  <span>Portfolio Impact</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    width="20" 
                    height="20" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="text-emerald-500"
                  >
                    <path d="M2 22c1.25-1.25 2.5-2.5 3.75-2.5 1.25 0 1.25 1.25 2.5 1.25 1.25 0 1.25-1.25 2.5-1.25 1.25 0 1.25 1.25 2.5 1.25 1.25 0 1.25-1.25 2.5-1.25 1.25 0 2.5 1.25 3.75 2.5"></path>
                    <path d="M20 17c-5.89-5.89-13.75-2.12-18.77 2.9"></path>
                    <path d="M20 7v10"></path>
                    <path d="M16 3v4h4"></path>
                    <path d="M16 3c-5 0-5 8-10 8"></path>
                  </svg>
                  <span>Sustainable Returns</span>
                </div>
              </div>
            </div>
            
            <div 
              className="flex-1 relative"
              style={{
                transform: `translateX(${Math.max(0, 100 - Math.max(0, scrollY - 300) * 0.5)}px)`,
                opacity: Math.min(1, Math.max(0, scrollY - 300) / 300),
                transition: 'transform 1s ease-out, opacity 1s ease-out',
              }}
            >
              <div 
                className={`relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl`}
                onMouseMove={(e) => handleMouseMove3D(e, 15)}
                onMouseLeave={resetCard}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={resetCard}
                style={{
                  background: theme === 'dark' ? 'rgba(20, 20, 20, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme === 'dark' 
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(16, 185, 129, 0.2)' 
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 0 30px rgba(16, 185, 129, 0.1)',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.2s ease-out',
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-4/5 h-4/5 p-6 transform" style={{ transform: 'translateZ(20px)' }}>
                    {/* Impact visualization */}
                    <div className="w-full h-full flex flex-col">
                      <div className="text-xl font-semibold mb-6 text-center">Selected UN Sustainable Development Goals (UN SDGs)</div>
                      
                      <div className="flex-1 flex flex-wrap justify-center items-center gap-4" style={{ transform: 'translateZ(40px)' }}>
                        {[
                          {number: 1, name: 'No Poverty', color: '#E5243B', description: 'End poverty in all its forms everywhere', targets: 7, url: 'https://sdgs.un.org/goals/goal1'},
                          {number: 3, name: 'Good Health', color: '#4C9F38', description: 'Ensure healthy lives and promote well-being for all at all ages', targets: 13, url: 'https://sdgs.un.org/goals/goal3'},
                          {number: 4, name: 'Quality Education', color: '#C5192D', description: 'Ensure inclusive and equitable quality education and promote lifelong learning opportunities for all', targets: 10, url: 'https://sdgs.un.org/goals/goal4'},
                          {number: 6, name: 'Clean Water', color: '#26BDE2', description: 'Ensure availability and sustainable management of water and sanitation for all', targets: 8, url: 'https://sdgs.un.org/goals/goal6'},
                          {number: 7, name: 'Affordable Energy', color: '#FCC30B', description: 'Ensure access to affordable, reliable, sustainable and modern energy for all', targets: 5, url: 'https://sdgs.un.org/goals/goal7'},
                          {number: 8, name: 'Decent Work', color: '#A21942', description: 'Promote sustained, inclusive and sustainable economic growth, full and productive employment and decent work for all', targets: 12, url: 'https://sdgs.un.org/goals/goal8'},
                          {number: 9, name: 'Industry & Innovation', color: '#FD6925', description: 'Build resilient infrastructure, promote inclusive and sustainable industrialization and foster innovation', targets: 8, url: 'https://sdgs.un.org/goals/goal9'},
                          {number: 11, name: 'Sustainable Cities', color: '#FD9D24', description: 'Make cities and human settlements inclusive, safe, resilient and sustainable', targets: 10, url: 'https://sdgs.un.org/goals/goal11'},
                          {number: 12, name: 'Responsible Consumption', color: '#BF8B2E', description: 'Ensure sustainable consumption and production patterns', targets: 11, url: 'https://sdgs.un.org/goals/goal12'},
                          {number: 13, name: 'Climate Action', color: '#3F7E44', description: 'Take urgent action to combat climate change and its impacts', targets: 5, url: 'https://sdgs.un.org/goals/goal13'}
                        ].map((goal, i) => (
                          <a 
                            key={i} 
                            href={goal.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative group cursor-pointer"
                            style={{
                              animation: `fadeIn 0.8s ${i * 0.1}s forwards`,
                              opacity: 0,
                            }}
                            onMouseEnter={() => setMouseEntered(true)}
                            onMouseLeave={() => setMouseEntered(false)}
                          >
                            <div 
                              className="w-24 h-24 md:w-28 md:h-28 rounded-lg flex items-center justify-center relative overflow-hidden"
                              style={{ 
                                backgroundColor: goal.color,
                                boxShadow: `0 5px 15px ${goal.color}50`,
                                transform: `scale(1) translateY(${Math.sin(Date.now()/1000 + i) * 5}px)`,
                                transition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out',
                                animation: `float ${3 + i % 2}s infinite ease-in-out alternate`,
                              }}
                            >
                              {/* Keep original image */}
                              <img
                                src={`Images/sdg${goal.number.toString().padStart(2, '0')}.png`}
                                alt={goal.name}
                                className="w-full h-full object-contain p-2"
                              />
                              
                              {/* Hover overlay with additional information */}
                              <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-90 transition-opacity duration-300 flex flex-col justify-center items-center p-2 text-xs text-white">
                                <div className="font-bold mb-1">Goal {goal.number}</div>
                                <div className="text-center line-clamp-3 mb-1">{goal.description}</div>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="px-2 py-0.5 rounded-full bg-white/20">{goal.targets} Targets</div>
                                  <div className="px-2 py-0.5 rounded-full bg-white/20">
                                    <ExternalLink size={10} className="inline mr-1" />
                                    View Details
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Animated border on hover */}
                            <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                              style={{
                                background: `linear-gradient(90deg, transparent, ${goal.color}, transparent)`,
                                backgroundSize: '200% 100%',
                                animation: 'shimmer 2s infinite',
                                filter: 'blur(4px)',
                                transform: 'scale(1.05)',
                                zIndex: -1
                              }}>
                            </div>
                          </a>
                        ))}
                      </div>
                      
                      <div className="mt-auto text-center">
                        <div className="text-sm opacity-70 mb-2">Impact Investment Focus Areas</div>
                        <div className="text-3xl font-bold text-green-500 flex items-center justify-center gap-2">
                          Sustainable Future
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section break: SDG intro above, Impact Vision panel below */}
          <div className="my-20 max-w-6xl mx-auto flex items-center gap-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-emerald-500/40 to-indigo-500/40" />
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-50 font-mono">framework</div>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent via-fuchsia-500/40 to-amber-500/40" />
          </div>

          {/* Impact Vision — distinct panel container */}
          <div className="relative mt-4 max-w-6xl mx-auto rounded-[28px] overflow-hidden"
               style={{
                 background: theme === 'dark'
                   ? 'linear-gradient(135deg, rgba(15,23,42,0.85) 0%, rgba(20,40,60,0.75) 50%, rgba(15,30,30,0.85) 100%)'
                   : 'linear-gradient(135deg, rgba(248,250,252,0.95) 0%, rgba(236,253,245,0.85) 50%, rgba(238,242,255,0.90) 100%)',
                 border: theme === 'dark' ? '1px solid rgba(99,102,241,0.25)' : '1px solid rgba(99,102,241,0.20)',
                 boxShadow: theme === 'dark'
                   ? '0 30px 80px -30px rgba(99,102,241,0.40), inset 0 1px 0 rgba(255,255,255,0.05)'
                   : '0 30px 80px -30px rgba(99,102,241,0.25), inset 0 1px 0 rgba(255,255,255,0.6)',
               }}>
            {/* Decorative ribbon stripe along the top */}
            <div className="h-1.5 w-full"
                 style={{ background: 'linear-gradient(90deg, #10b981, #14b8a6, #0ea5e9, #6366f1, #a855f7, #ec4899, #f59e0b)' }} />

            {/* Decorative corner glows */}
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
                 style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.30), transparent 70%)' }} />
            <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full pointer-events-none"
                 style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.20), transparent 70%)' }} />

            <div className="relative p-6 md:p-10 lg:p-14">
              {/* Header with GitHub branding */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase"
                         style={{
                           background: 'linear-gradient(90deg, rgba(16,185,129,0.20), rgba(99,102,241,0.20))',
                           color: '#10b981',
                           border: '1px solid rgba(16,185,129,0.40)',
                         }}>
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                      </span>
                      Open Source · v1
                    </div>
                    <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono tracking-wider"
                         style={{ background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.30)' }}>
                      MIT
                    </div>
                  </div>
                  <h3 className="text-4xl md:text-6xl font-extrabold leading-[1.1] tracking-tight">
                    <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-transparent bg-clip-text">Impact</span>{' '}
                    <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-pink-400 text-transparent bg-clip-text">Vision</span>
                  </h3>
                  <div className="mt-2 text-sm md:text-base opacity-75 max-w-2xl">
                    A framework for authentic impact — moving beyond intention to{' '}
                    <span className="font-semibold text-emerald-400">measurable</span>,{' '}
                    <span className="font-semibold text-sky-400">standardised</span>, and{' '}
                    <span className="font-semibold text-fuchsia-400">credible</span> assessment.
                  </div>
                </div>

                <a
                  href="https://github.com/joejoe168168/impact-vision"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 rounded-xl px-4 py-3 self-start transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: theme === 'dark' ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.95)',
                    border: '1px solid rgba(99,102,241,0.40)',
                    boxShadow: '0 10px 30px -10px rgba(99,102,241,0.40)',
                  }}
                >
                  <Github size={20} className="text-indigo-400" />
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-widest opacity-60 -mb-0.5">View repo</div>
                    <div className="text-sm font-bold font-mono">joejoe168168/<span className="text-indigo-400">impact-vision</span></div>
                  </div>
                  <ArrowRight size={16} className="opacity-60 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />
                </a>
              </div>

              {/* Intro definition */}
              <div className="rounded-2xl p-5 mb-12"
                   style={{
                     background: theme === 'dark' ? 'rgba(15,23,42,0.55)' : 'rgba(255,255,255,0.55)',
                     border: '1px solid rgba(99,102,241,0.18)',
                     backdropFilter: 'blur(8px)',
                   }}>
                <p className="text-sm md:text-base leading-relaxed opacity-85">
                  Impact investing is{' '}
                  <span className="font-semibold text-emerald-400">investing with the intention to generate positive, measurable social and environmental impact alongside a financial return</span>{' '}
                  — distinct from traditional investing (returns only) and philanthropy (impact only).
                </p>
              </div>

              {/* Five Dimensions — multi-color palette */}
              <div className="mb-12">
                <div className="flex items-center justify-center gap-3 mb-8">
                  <div className="h-px flex-1 max-w-16 bg-gradient-to-r from-transparent to-emerald-500/50" />
                  <div className="text-xs uppercase tracking-[0.3em] opacity-80 font-semibold">The 5 Dimensions of Impact</div>
                  <div className="h-px flex-1 max-w-16 bg-gradient-to-l from-transparent to-amber-500/50" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {[
                    {
                      icon: <Target size={22} />, num: '01', color: '#10b981',
                      title: 'What outcome?',
                      body: 'Specificity of the claimed change — vague intentions don\'t qualify.',
                      example: 'A solar deal claiming "supports clean energy" fails — but "displaces 1,200 t CO₂e/yr from diesel gensets in 8 East African clinics" passes.',
                      metric: 'IRIS+ PI4060 · GHG emissions avoided',
                    },
                    {
                      icon: <Globe size={22} />, num: '02', color: '#14b8a6',
                      title: 'Who benefits?',
                      body: 'Identification and depth of beneficiary reach.',
                      example: '"The community" is not a beneficiary. "342 smallholder farmers earning <$3/day in Kakamega County" is.',
                      metric: 'IRIS+ OI6376 · Beneficiaries served',
                    },
                    {
                      icon: <BarChart3 size={22} />, num: '03', color: '#0ea5e9',
                      title: 'How much?',
                      body: 'Scale and magnitude of the impact delivered.',
                      example: 'Reach AND depth: a literacy app used by 2M kids for 5 minutes is not equivalent to one used by 50K kids for 200 hours.',
                      metric: 'Reach × Depth × Duration',
                    },
                    {
                      icon: <Lightbulb size={22} />, num: '04', color: '#a855f7',
                      title: 'Would it happen anyway?',
                      body: 'Additionality and counterfactual analysis.',
                      example: 'Funding a solar project the government already had budgeted is replacement, not impact. Funding the next project that wouldn\'t exist without you is.',
                      metric: 'Counterfactual / RCT / quasi-experiment',
                    },
                    {
                      icon: <Shield size={22} />, num: '05', color: '#f59e0b',
                      title: 'What could go wrong?',
                      body: 'Risk identification and mitigation.',
                      example: 'A microfinance fund that cuts default rates by raising rates on the poorest borrowers has shifted, not reduced, harm. Disclose it.',
                      metric: 'Adverse impact register · IFC PS',
                    },
                  ].map((d, i) => (
                    <DimensionFlipCard key={d.num} d={d} theme={theme} idx={i} />
                  ))}
                </div>
              </div>

              {/* Core Principles — varied palette */}
              <div className="mb-12">
                <div className="flex items-center justify-center gap-3 mb-8">
                  <div className="h-px flex-1 max-w-16 bg-gradient-to-r from-transparent to-sky-500/50" />
                  <div className="text-xs uppercase tracking-[0.3em] opacity-80 font-semibold">Guiding Principles</div>
                  <div className="h-px flex-1 max-w-16 bg-gradient-to-l from-transparent to-fuchsia-500/50" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[
                    {
                      title: 'Evidence-Based Measurement',
                      body: 'Rigorous evidence standards (NESTA Standards of Evidence levels 1–5), from narrative accounts to randomised controlled trials. Minimum-metric gates prevent score inflation.',
                      accent: '#10b981',
                      tag: 'Rigour',
                    },
                    {
                      title: 'Standardisation & Transparency',
                      body: 'Built on GIIN\'s IRIS+ framework — ~787 metrics that act as the "GAAP for impact," ensuring consistent measurement across the investment ecosystem.',
                      accent: '#0ea5e9',
                      tag: 'Standards',
                    },
                    {
                      title: 'Anti-Greenwashing Credibility',
                      body: 'Composite scoring detects vague claims, adverse omissions, and specificity gaps — favouring authentic impact over marketing narratives.',
                      accent: '#ec4899',
                      tag: 'Credibility',
                    },
                    {
                      title: 'Multi-Stakeholder Alignment',
                      body: 'Coverage spans 10 ESG frameworks, 17 UN SDGs, and governance structures — recognising impact is a coordination problem across beneficiaries, investors, and regulators.',
                      accent: '#f59e0b',
                      tag: 'Alignment',
                    },
                  ].map((p, i) => (
                    <div
                      key={p.title}
                      className="relative rounded-2xl p-6 overflow-hidden group transition-all duration-300 hover:-translate-y-1"
                      style={{
                        background: theme === 'dark'
                          ? `linear-gradient(135deg, ${p.accent}10, rgba(15,23,42,0.55))`
                          : `linear-gradient(135deg, ${p.accent}10, rgba(255,255,255,0.70))`,
                        border: `1px solid ${p.accent}40`,
                        backdropFilter: 'blur(8px)',
                        animation: `slideUp 0.6s ease-out ${i * 0.08}s both`,
                      }}
                    >
                      <div className="absolute left-0 top-0 bottom-0 w-1"
                           style={{ background: `linear-gradient(to bottom, ${p.accent}, ${p.accent}33)` }} />
                      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-40 pointer-events-none transition-opacity duration-500 group-hover:opacity-70"
                           style={{ background: `radial-gradient(circle, ${p.accent}30, transparent 70%)` }} />
                      <div className="relative flex items-center justify-between mb-2">
                        <div className="font-semibold text-lg" style={{ color: p.accent }}>{p.title}</div>
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
                              style={{ background: `${p.accent}22`, color: p.accent, border: `1px solid ${p.accent}55` }}>
                          {p.tag}
                        </span>
                      </div>
                      <div className="relative text-sm opacity-85 leading-relaxed">{p.body}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coverage stats — varied colors with animated counters */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                {[
                  { num: 787, label: 'IRIS+ metrics',   sub: 'GIIN standardised library', accent: '#10b981', from: '#10b981', to: '#22c55e' },
                  { num: 17,  label: 'UN SDGs',         sub: 'Mapped & weighted',          accent: '#0ea5e9', from: '#0ea5e9', to: '#a855f7' },
                  { num: 10,  label: 'ESG frameworks',  sub: 'Cross-walk coverage',        accent: '#ec4899', from: '#ec4899', to: '#f59e0b' },
                  { num: 5,   label: 'Evidence levels', sub: 'NESTA standards',            accent: '#f59e0b', from: '#f59e0b', to: '#ef4444' },
                ].map((s, i) => (
                  <div
                    key={s.label}
                    className="relative rounded-2xl p-5 text-center group transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                    style={{
                      background: theme === 'dark'
                        ? `linear-gradient(135deg, ${s.accent}18, rgba(15,23,42,0.55))`
                        : `linear-gradient(135deg, ${s.accent}10, rgba(255,255,255,0.70))`,
                      border: `1px solid ${s.accent}55`,
                      animation: `slideUp 0.6s ease-out ${i * 0.08}s both`,
                    }}
                  >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                         style={{ background: `radial-gradient(circle at 50% 0%, ${s.accent}33, transparent 70%)` }} />
                    <div className="relative text-4xl md:text-6xl font-extrabold leading-none tracking-tight"
                         style={{
                           backgroundImage: `linear-gradient(135deg, ${s.from}, ${s.to})`,
                           WebkitBackgroundClip: 'text',
                           backgroundClip: 'text',
                           color: 'transparent',
                           filter: `drop-shadow(0 4px 18px ${s.accent}55)`,
                         }}>
                      <AnimatedCounter target={s.num} />
                    </div>
                    <div className="relative text-sm font-semibold mt-2" style={{ color: s.accent }}>{s.label}</div>
                    <div className="relative text-xs opacity-60 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* Interactive Score Yourself widget */}
              <ScoreYourselfWidget theme={theme} />
            </div>
          </div>

          {/* Selected Writing — distinctly framed sub-section */}
          <div className="mt-20 max-w-6xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-20 bg-gradient-to-r from-transparent via-indigo-500/40 to-pink-500/40" />
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-[0.2em] uppercase"
                   style={{
                     background: theme === 'dark' ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.08)',
                     border: '1px solid rgba(99,102,241,0.30)',
                     color: '#a5b4fc',
                   }}>
                <Linkedin size={12} /> Selected Writing
              </div>
              <div className="h-px flex-1 max-w-20 bg-gradient-to-l from-transparent via-pink-500/40 to-indigo-500/40" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  tag: 'Circular Economy',
                  accent: '#10b981',
                  title: 'China\'s 1.2M-ton solar recycling gap',
                  body: 'China is set to retire 1.2 million tons of solar panels in 2025 — but only has formal recycling capacity for 300,000 of them. The largest circular-economy gap in history, and a 420 billion RMB opportunity.',
                  url: 'https://www.linkedin.com/pulse/solar-panel-recycling-turning-chinas-looming-wave-420-joseph-aef6c',
                },
                {
                  tag: 'Energy Transition',
                  accent: '#0ea5e9',
                  title: 'Beyond EVs: the next fuel revolution',
                  body: 'Electric vehicles can\'t fly 10,000 km or move 90% of global trade. The next wave of decarbonisation belongs to SAF, green methanol and e-fuels — and Hong Kong\'s EcoCeres is one of the names leading it.',
                  url: 'https://www.linkedin.com/pulse/beyond-batteries-evs-fuels-powering-planes-ships-more-joseph-72hec',
                },
                {
                  tag: 'Maritime',
                  accent: '#14b8a6',
                  title: '80% of trade, <1% of clean-energy investment',
                  body: 'Shipping carries over 80% of world trade — yet attracts less than 1% of global clean-energy investment. Inspired by Prof. Jeffrey Sachs, why maritime decarbonisation may be climate finance\'s most underfunded opportunity.',
                  url: 'https://www.linkedin.com/pulse/most-underrated-climate-fight-joseph-leung-caia-cfa-cesga-fcpa-6pyic',
                },
                {
                  tag: 'Measurement',
                  accent: '#a855f7',
                  title: 'Impact isn\'t hope — it\'s measurement',
                  body: 'Pairing GIIN\'s IRIS+ framework with a real-world Second Chance Programme that hires people with criminal records. Real impact, real data, real lives changed — not just good intentions on a deck.',
                  url: 'https://www.linkedin.com/pulse/impact-measurement-001-measuring-what-actually-moves-joseph-zz1fc',
                },
                {
                  tag: 'Carbon Markets',
                  accent: '#f59e0b',
                  title: '$30 → $100+: COP26 and the carbon-capture boom',
                  body: 'How COP26 in Glasgow turned carbon capture into one of the planet\'s hottest investment themes — with names like Carbon Clean Solutions repricing in months. What it tells us about timing climate-policy beta.',
                  url: 'https://www.linkedin.com/pulse/year-climate-tech-finally-got-paid-cop26-100-credit-joseph-vzsvc',
                },
              ].map((post, i) => (
                <a
                  key={post.url}
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={() => setMouseEntered(true)}
                  onMouseLeave={() => setMouseEntered(false)}
                  className="group relative rounded-2xl p-5 flex flex-col transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                  style={{
                    background: theme === 'dark'
                      ? `linear-gradient(135deg, ${post.accent}10, rgba(20,25,40,0.55))`
                      : `linear-gradient(135deg, ${post.accent}10, rgba(255,255,255,0.70))`,
                    border: `1px solid ${post.accent}35`,
                    backdropFilter: 'blur(8px)',
                    animation: `slideUp 0.6s ease-out ${i * 0.08}s both`,
                  }}
                >
                  <div className="absolute top-0 left-0 right-0 h-0.5"
                       style={{ background: `linear-gradient(90deg, ${post.accent}, transparent)` }} />
                  <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none opacity-50 transition-opacity duration-300 group-hover:opacity-80"
                       style={{ background: `radial-gradient(circle, ${post.accent}30, transparent 70%)` }} />
                  <div className="relative flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded"
                          style={{ background: `${post.accent}22`, color: post.accent, border: `1px solid ${post.accent}55` }}>
                      {post.tag}
                    </span>
                    <Linkedin size={14} className="text-indigo-400 opacity-70 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="relative font-semibold text-base mb-2 leading-snug transition-colors"
                       style={{ color: 'inherit' }}>
                    {post.title}
                  </div>
                  <div className="relative text-sm opacity-75 leading-relaxed mb-4 flex-1">
                    {post.body}
                  </div>
                  <div className="relative flex items-center gap-1.5 text-xs font-medium transition-colors" style={{ color: post.accent }}>
                    Read on LinkedIn <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </a>
              ))}

              {/* Repo CTA card */}
              <a
                href="https://github.com/joejoe168168/impact-vision"
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setMouseEntered(true)}
                onMouseLeave={() => setMouseEntered(false)}
                className="group relative rounded-2xl p-5 flex flex-col justify-between overflow-hidden transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'linear-gradient(135deg, rgba(99,102,241,0.20), rgba(168,85,247,0.10))',
                  border: '1px solid rgba(99,102,241,0.45)',
                  boxShadow: '0 10px 30px -10px rgba(99,102,241,0.40)',
                  animation: `slideUp 0.6s ease-out ${5 * 0.08}s both`,
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-0.5"
                     style={{ background: 'linear-gradient(90deg, #6366f1, #a855f7, #ec4899)' }} />
                <div>
                  <div className="flex items-center gap-2 mb-3 text-indigo-400">
                    <Github size={18} />
                    <span className="text-[10px] uppercase tracking-widest font-bold">Open Source</span>
                  </div>
                  <div className="font-bold text-base mb-2 leading-snug">
                    Explore the Impact Vision repo
                  </div>
                  <div className="text-sm opacity-80 leading-relaxed mb-4">
                    Frameworks, scoring rubrics and evidence standards — open to contribution and critique.
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-400">
                  View on GitHub <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section id="ai" className={`min-h-screen flex items-center relative py-24 ${theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-200/50'} backdrop-blur-md`}>
        <SectionBackdrop theme={theme} variant="ai" />
        <div className={`absolute inset-0 bg-gradient-to-b ${theme === 'dark' ? 'from-gray-900/60 via-transparent to-gray-900/60' : 'from-gray-100/60 via-transparent to-gray-100/60'}`} />

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 relative">
              <div className={`absolute -top-10 -left-10 text-9xl opacity-5 font-bold ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>02</div>
              <div 
                className="overflow-hidden mb-4"
                style={{
                  transform: `translateY(${Math.max(0, 100 - Math.max(0, scrollY - 1500) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 1500) / 200),
                  transition: 'transform 0.8s ease-out, opacity 0.8s ease-out',
                }}
              >
                <h2 className="text-4xl md:text-6xl font-bold mb-2 relative bg-gradient-to-r from-blue-400 to-indigo-600 text-transparent bg-clip-text">
                  AI Explorer
                </h2>
                <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-indigo-700 rounded-full" />
              </div>
              
              <div 
                className="text-lg mb-6 opacity-80 max-w-xl"
                style={{
                  transform: `translateY(${Math.max(0, 120 - Math.max(0, scrollY - 1500) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 1500) / 250),
                  transition: 'transform 1s ease-out, opacity 1s ease-out',
                }}
              >
                <p>Venturing into the cutting edge of artificial intelligence by experimenting with various Large Language Models (LLMs) and AI technologies. I explore different protocols including MCP, Vibe coding methodologies, and creative image generation. My focus is on developing intuitive AI interfaces and pioneering new frameworks for effective human-AI collaboration.</p>
              </div>
              
              <div 
                className="flex flex-wrap gap-3 mb-8"
                style={{
                  transform: `translateY(${Math.max(0, 140 - Math.max(0, scrollY - 1500) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 1500) / 300),
                  transition: 'transform 1.2s ease-out, opacity 1.2s ease-out',
                }}
              >
                {['LLM', 'MCP', 'Vibe Coding', 'Image Generation', 'AI Agents'].map((tag, i) => (
                  <span 
                    key={tag} 
                    className={`px-4 py-1 rounded-full text-sm relative overflow-hidden group`}
                    style={{
                      transition: 'all 0.3s ease-out',
                      transitionDelay: `${i * 100}ms`,
                    }}
                  >
                    <span className={`absolute inset-0 ${theme === 'dark' ? 'bg-blue-900/60' : 'bg-blue-100'} group-hover:scale-110 transition-transform duration-300`}></span>
                    <span className="relative z-10">{tag}</span>
                  </span>
                ))}
              </div>
              
              <div 
                className="flex items-center gap-6"
                style={{
                  transform: `translateY(${Math.max(0, 160 - Math.max(0, scrollY - 1500) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 1500) / 350),
                  transition: 'transform 1.4s ease-out, opacity 1.4s ease-out',
                }}
              >
                <div className="flex items-center gap-2">
                  <Cpu size={20} className="text-blue-500" />
                  <span>Future Technology</span>
                </div>
                <div className="flex items-center gap-2">
                  <Circle size={20} className="text-blue-500" />
                  <span>Human-AI Collaboration</span>
                </div>
              </div>
            </div>
            
            <div 
              className="flex-1 relative"
              style={{
                transform: `translateX(${Math.max(0, 100 - Math.max(0, scrollY - 1500) * 0.5)}px)`,
                opacity: Math.min(1, Math.max(0, scrollY - 1500) / 300),
                transition: 'transform 1s ease-out, opacity 1s ease-out',
              }}
            >
              <div 
                className={`relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl`}
                onMouseMove={(e) => handleMouseMove3D(e, 15)}
                onMouseLeave={resetCard}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={resetCard}
                style={{
                  background: theme === 'dark' ? 'rgba(20, 20, 20, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme === 'dark' 
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(59, 130, 246, 0.2)' 
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 0 30px rgba(59, 130, 246, 0.1)',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.2s ease-out',
                }}
              >
                <div className="absolute inset-0 p-5 sm:p-6 flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blue-400/90">
                      <Sparkles size={12} className="animate-pulse" /> Inference
                    </div>
                    <div className="text-[10px] opacity-60 font-mono flex items-center gap-1">
                      <Bot size={10} /> agent.run
                    </div>
                  </div>

                  {/* Prompt bubble */}
                  <div
                    className="rounded-xl px-3 py-2 text-xs mb-2 self-start max-w-[85%]"
                    style={{
                      background: theme === 'dark' ? 'rgba(30,40,60,0.55)' : 'rgba(239,246,255,0.85)',
                      border: '1px solid rgba(99,102,241,0.25)',
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5 text-[9px] uppercase tracking-wider opacity-70">
                      <MessageSquare size={9} /> Prompt
                    </div>
                    <span className="ai-typed-prompt"></span>
                  </div>

                  {/* Neural network — 4-layer transformer-style */}
                  <div className="flex-1 relative my-2 flex items-center justify-center">
                    <svg viewBox="0 0 320 180" className="w-full h-full" style={{ overflow: 'visible' }}>
                      <defs>
                        <linearGradient id="aiLine" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="rgba(59,130,246,0)" />
                          <stop offset="50%" stopColor="rgba(99,102,241,0.7)" />
                          <stop offset="100%" stopColor="rgba(59,130,246,0)" />
                        </linearGradient>
                        <radialGradient id="aiNode">
                          <stop offset="0%" stopColor="#60a5fa" stopOpacity="1" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.4" />
                        </radialGradient>
                        <radialGradient id="aiNodeCore">
                          <stop offset="0%" stopColor="#a78bfa" stopOpacity="1" />
                          <stop offset="100%" stopColor="#6366f1" stopOpacity="0.4" />
                        </radialGradient>
                      </defs>

                      {(() => {
                        const layers = [3, 5, 5, 3];
                        const xs = [40, 130, 220, 290];
                        const lines = [];
                        const nodes = [];
                        layers.forEach((count, li) => {
                          for (let i = 0; i < count; i++) {
                            const cy = (180 / (count + 1)) * (i + 1);
                            const cx = xs[li];
                            const isCore = li === 1 || li === 2;
                            nodes.push(
                              <g key={`n-${li}-${i}`}>
                                <circle cx={cx} cy={cy} r={isCore ? 7 : 5}
                                        fill={isCore ? 'url(#aiNodeCore)' : 'url(#aiNode)'}
                                        style={{ filter: `drop-shadow(0 0 6px ${isCore ? 'rgba(167,139,250,0.6)' : 'rgba(96,165,250,0.6)'})`, animation: `pulseNode ${2 + (li + i) * 0.3}s ease-in-out infinite alternate` }} />
                                <circle cx={cx} cy={cy} r="2" fill="white" opacity="0.9" />
                              </g>
                            );
                            // connect to next layer
                            if (li < layers.length - 1) {
                              const nextCount = layers[li + 1];
                              for (let j = 0; j < nextCount; j++) {
                                const cy2 = (180 / (nextCount + 1)) * (j + 1);
                                const cx2 = xs[li + 1];
                                lines.push(
                                  <line key={`l-${li}-${i}-${j}`}
                                        x1={cx} y1={cy} x2={cx2} y2={cy2}
                                        stroke="rgba(99,102,241,0.18)" strokeWidth="0.6">
                                    <animate attributeName="stroke-opacity" values="0.08;0.45;0.08" dur={`${3 + ((li + i + j) % 3)}s`} repeatCount="indefinite" />
                                  </line>
                                );
                              }
                            }
                          }
                        });
                        // Token particles flowing left to right along center
                        const particles = [...Array(6)].map((_, i) => (
                          <circle key={`p-${i}`} r="2.5" fill="#a78bfa" opacity="0.85"
                                  style={{ filter: 'drop-shadow(0 0 6px #a78bfa)' }}>
                            <animateMotion dur={`${3 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.6}s`}
                                           path={`M 40 ${30 + i * 22} Q 160 90 290 ${50 + (i % 3) * 30}`} />
                            <animate attributeName="opacity" values="0;1;1;0" dur={`${3 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.6}s`} />
                          </circle>
                        ));
                        return (
                          <>
                            <g opacity="0.9">{lines}</g>
                            {nodes}
                            {particles}
                          </>
                        );
                      })()}

                      {/* Layer labels */}
                      <text x="40" y="172" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.5">tokens</text>
                      <text x="130" y="172" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.5">attention</text>
                      <text x="220" y="172" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.5">reasoning</text>
                      <text x="290" y="172" textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.5">output</text>
                    </svg>
                  </div>

                  {/* Output bubble */}
                  <div
                    className="rounded-xl px-3 py-2 text-xs mb-3 self-end max-w-[85%]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(59,130,246,0.10))',
                      border: '1px solid rgba(167,139,250,0.35)',
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5 text-[9px] uppercase tracking-wider text-indigo-300">
                      <Bot size={9} /> Response
                    </div>
                    <span className="ai-typed-response"></span>
                  </div>

                  {/* Capability badges */}
                  <div className="grid grid-cols-5 gap-1.5">
                    {[
                      { name: 'LLM',     Icon: Brain,   color: '#3b82f6' },
                      { name: 'MCP',     Icon: Layers,  color: '#6366f1' },
                      { name: 'Vibe',    Icon: Wand2,   color: '#a855f7' },
                      { name: 'Image',   Icon: ImageIcon, color: '#06b6d4' },
                      { name: 'Agents',  Icon: Code2,   color: '#0ea5e9' },
                    ].map((b, i) => (
                      <div
                        key={b.name}
                        className="rounded-lg p-1.5 flex flex-col items-center justify-center gap-0.5 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                        style={{
                          background: theme === 'dark' ? 'rgba(20,28,48,0.55)' : 'rgba(239,246,255,0.7)',
                          border: `1px solid ${b.color}33`,
                          animation: `slideUp 0.5s ease-out ${i * 0.05}s both`,
                        }}
                      >
                        <b.Icon size={14} style={{ color: b.color }} />
                        <span className="text-[9px] font-semibold opacity-80">{b.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bazi Section */}
      <section id="bazi" className="min-h-screen flex items-center relative py-24">
        <SectionBackdrop theme={theme} variant="bazi" />
        <div className={`absolute inset-0 bg-gradient-to-b ${theme === 'dark' ? 'from-gray-900/60 via-transparent to-gray-900/60' : 'from-gray-100/60 via-transparent to-gray-100/60'}`} />

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row-reverse items-center gap-12">
            <div className="flex-1 relative">
              <div className={`absolute -top-10 -left-10 text-9xl opacity-5 font-bold ${theme === 'dark' ? 'text-purple-300' : 'text-purple-700'}`}>03</div>
              <div 
                className="overflow-hidden mb-4"
                style={{
                  transform: `translateY(${Math.max(0, 100 - Math.max(0, scrollY - 2100) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 2100) / 200),
                  transition: 'transform 0.8s ease-out, opacity 0.8s ease-out',
                }}
              >
                <h2 className="text-4xl md:text-6xl font-bold mb-2 relative">
                  <span className="bg-gradient-to-r from-purple-400 to-fuchsia-600 text-transparent bg-clip-text">Bazi Practitioner</span>
                  <span className="ml-3 text-2xl opacity-70">八字</span>
                </h2>
                <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-fuchsia-700 rounded-full" />
              </div>
              
              <div 
                className="text-lg mb-6 opacity-80 max-w-xl"
                style={{
                  transform: `translateY(${Math.max(0, 120 - Math.max(0, scrollY - 2100) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 2100) / 250),
                  transition: 'transform 1s ease-out, opacity 1s ease-out',
                }}
              >
                <p>Practicing the ancient Chinese metaphysical science of fate calculation based on the Four Pillars of Destiny. Bazi reveals innate personality traits and helps to "attract good fortune while avoiding misfortune" (趨吉避凶). This wisdom extends to Feng Shui principles, offering holistic guidance on harmonizing environments with personal energies and cosmic patterns.</p>
              </div>
              
              <div 
                className="flex flex-wrap gap-3 mb-8"
                style={{
                  transform: `translateY(${Math.max(0, 140 - Math.max(0, scrollY - 2100) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 2100) / 300),
                  transition: 'transform 1.2s ease-out, opacity 1.2s ease-out',
                }}
              >
                {['Four Pillars', 'Chinese Metaphysics', 'Personality Analysis', 'Feng Shui', 'Destiny Reading'].map((tag, i) => (
                  <span 
                    key={tag} 
                    className={`px-4 py-1 rounded-full text-sm relative overflow-hidden group`}
                    style={{
                      transition: 'all 0.3s ease-out',
                      transitionDelay: `${i * 100}ms`,
                    }}
                  >
                    <span className={`absolute inset-0 ${theme === 'dark' ? 'bg-purple-900/60' : 'bg-purple-100'} group-hover:scale-110 transition-transform duration-300`}></span>
                    <span className="relative z-10">{tag}</span>
                  </span>
                ))}
              </div>
              
              <div 
                className="flex items-center gap-6"
                style={{
                  transform: `translateY(${Math.max(0, 160 - Math.max(0, scrollY - 2100) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 2100) / 350),
                  transition: 'transform 1.4s ease-out, opacity 1.4s ease-out',
                }}
              >
                <div className="flex items-center gap-2">
                  <Moon size={20} className="text-purple-500" />
                  <span>Cosmic Patterns</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star size={20} className="text-purple-500" />
                  <span>Personal Destiny</span>
                </div>
              </div>
            </div>
            
            <div 
              className="flex-1 relative"
              style={{
                opacity: Math.min(1, Math.max(0, scrollY - 2100) / 300), // Keep fade-in effect
                transition: 'opacity 1s ease-out', // Remove transform
              }}
            >
              <div 
                className={`relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl`}
                onMouseMove={(e) => handleMouseMove3D(e, 15)}
                onMouseLeave={resetCard}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={resetCard}
                style={{
                  background: theme === 'dark' ? 'rgba(20, 20, 20, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme === 'dark' 
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(168, 85, 247, 0.2)' 
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 0 30px rgba(168, 85, 247, 0.1)',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.2s ease-out',
                }}
              >
                <div className="absolute inset-0 p-5 sm:p-6 flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-fuchsia-400/90">
                      <Compass size={12} className="animate-pulse" /> Cosmic Compass
                    </div>
                    <BaziHourBadge theme={theme} />
                  </div>

                  {/* Life Relationships Wheel — Day Master at center, life domains around */}
                  <BaziLifeWheel theme={theme} />

                  {/* BaZi 4-pillar chart */}
                  <div className="rounded-xl p-2 backdrop-blur-sm grid grid-cols-4 gap-1.5 mt-3"
                       style={{
                         background: theme === 'dark' ? 'rgba(30,20,45,0.55)' : 'rgba(255,255,255,0.55)',
                         border: '1px solid rgba(168,85,247,0.30)',
                         transform: 'translateZ(40px)',
                       }}>
                    {[
                      { pillar: '年柱', label: 'Year', heavenlyStem: '丁', earthlyBranch: '丑' },
                      { pillar: '月柱', label: 'Month', heavenlyStem: '丙', earthlyBranch: '午' },
                      { pillar: '日柱', label: 'Day', heavenlyStem: '甲', earthlyBranch: '辰' },
                      { pillar: '时柱', label: 'Hour', heavenlyStem: '甲', earthlyBranch: '子' },
                    ].map((data) => {
                      const stemInfo = getElementInfo(data.heavenlyStem);
                      const branchInfo = getElementInfo(data.earthlyBranch);
                      return (
                        <div key={data.pillar}
                             className="rounded-lg overflow-hidden flex flex-col group transition-transform hover:scale-[1.04]"
                             style={{
                               background: theme === 'dark' ? 'rgba(20,12,30,0.5)' : 'rgba(255,255,255,0.6)',
                               border: '1px solid rgba(168,85,247,0.18)',
                             }}>
                          <div className="text-center py-1 text-[10px] tracking-wider uppercase font-semibold text-purple-400 border-b border-purple-400/15">
                            {data.label} <span className="opacity-60">{data.pillar}</span>
                          </div>
                          <div className="flex-1 flex flex-col items-center justify-center gap-1.5 py-2">
                            <div className="text-2xl font-semibold" style={{ color: stemInfo.color, textShadow: `0 0 12px ${stemInfo.color}55` }}
                                 title={`Stem element: ${stemInfo.name}`}>
                              {data.heavenlyStem}
                            </div>
                            <div className="text-2xl font-semibold" style={{ color: branchInfo.color, textShadow: `0 0 12px ${branchInfo.color}55` }}
                                 title={`Branch element: ${branchInfo.name}`}>
                              {data.earthlyBranch}
                            </div>
                          </div>
                          <div className="flex items-center justify-center gap-1 px-1 py-1.5 border-t border-purple-400/15">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ backgroundColor: `${stemInfo.color}25`, color: stemInfo.color }}>
                              {stemInfo.name}
                            </span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ backgroundColor: `${branchInfo.color}25`, color: branchInfo.color }}>
                              {branchInfo.name}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crypto Section */}
      <section id="crypto" className="min-h-screen flex items-center relative py-24">
        <SectionBackdrop theme={theme} variant="crypto" />
        <div className={`absolute inset-0 bg-gradient-to-b ${theme === 'dark' ? 'from-gray-900/60 via-transparent to-gray-900/60' : 'from-gray-100/60 via-transparent to-gray-100/60'}`} />

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row-reverse items-center gap-12">
            <div className="flex-1 relative">
              <div className={`absolute -top-10 -left-10 text-9xl opacity-5 font-bold ${theme === 'dark' ? 'text-orange-300' : 'text-orange-700'}`}>04</div>
              <div 
                className="overflow-hidden mb-4"
                style={{
                  transform: `translateY(${Math.max(0, 100 - Math.max(0, scrollY - 900) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 900) / 200),
                  transition: 'transform 0.8s ease-out, opacity 0.8s ease-out',
                }}
              >
                <h2 className="text-4xl md:text-6xl font-bold mb-2 relative bg-gradient-to-r from-orange-400 to-amber-600 text-transparent bg-clip-text">
                  Crypto Enthusiast
                </h2>
                <div className="h-1 w-24 bg-gradient-to-r from-orange-500 to-amber-700 rounded-full" />
              </div>
              
              <div 
                className="text-lg mb-6 opacity-80 max-w-xl"
                style={{
                  transform: `translateY(${Math.max(0, 120 - Math.max(0, scrollY - 900) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 900) / 250),
                  transition: 'transform 1s ease-out, opacity 1s ease-out',
                }}
              >
                <p>Navigating the frontier of decentralized finance(DeFi), blockchain technology, and digital assets to reshape the future of value exchange. From pioneering DeFi protocols to emerging memecoins, I actively participate across the evolving crypto ecosystem.</p>
              </div>
              
              <div 
                className="flex flex-wrap gap-3 mb-8"
                style={{
                  transform: `translateY(${Math.max(0, 140 - Math.max(0, scrollY - 900) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 900) / 300),
                  transition: 'transform 1.2s ease-out, opacity 1.2s ease-out',
                }}
              >
                {['Blockchain', 'DeFi', 'Web3', 'Memecoins', 'Digital Assets'].map((tag, i) => (
                  <span 
                    key={tag} 
                    className={`px-4 py-1 rounded-full text-sm relative overflow-hidden group`}
                    style={{
                      transition: 'all 0.3s ease-out',
                      transitionDelay: `${i * 100}ms`,
                    }}
                  >
                    <span className={`absolute inset-0 ${theme === 'dark' ? 'bg-orange-900/60' : 'bg-orange-100'} group-hover:scale-110 transition-transform duration-300`}></span>
                    <span className="relative z-10">{tag}</span>
                  </span>
                ))}
              </div>
              
              <div 
                className="flex items-center gap-6"
                style={{
                  transform: `translateY(${Math.max(0, 160 - Math.max(0, scrollY - 900) * 0.5)}px)`,
                  opacity: Math.min(1, Math.max(0, scrollY - 900) / 350),
                  transition: 'transform 1.4s ease-out, opacity 1.4s ease-out',
                }}
              >
                <div className="flex items-center gap-2">
                  <Circle size={20} className="text-orange-500" />
                  <span>Decentralized Systems</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign size={20} className="text-orange-500" />
                  <span>Digital Assets</span>
                </div>
              </div>
            </div>
            
            <div 
              className="flex-1 relative"
              style={{
                opacity: Math.min(1, Math.max(0, scrollY - 900) / 300), // Keep fade-in effect
                transition: 'opacity 1s ease-out', // Remove transform from here
              }}
            >
              <div 
                className={`relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl`}
                onMouseMove={(e) => handleMouseMove3D(e, 15)}
                onMouseLeave={resetCard}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={resetCard}
                style={{
                  background: theme === 'dark' ? 'rgba(20, 20, 20, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme === 'dark' 
                    ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(237, 137, 54, 0.2)' 
                    : '0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 0 30px rgba(237, 137, 54, 0.1)',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.2s ease-out',
                }}
              >
                <div className="absolute inset-0 p-5 sm:p-6 flex flex-col">
                  {/* Top row: live highlights for top 3 coins */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-400/90">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                      </span>
                      Markets Live
                    </div>
                    <div className="text-[10px] opacity-60 flex items-center gap-1">
                      <Activity size={10} /> CoinGecko
                    </div>
                  </div>
                  <div id="crypto-top-coins" className="grid grid-cols-3 gap-2 mb-4">
                    {/* Skeleton — replaced by JS once data arrives */}
                    {['BTC', 'ETH', 'SOL'].map((s) => (
                      <div
                        key={s}
                        className="rounded-xl px-3 py-2 border border-amber-500/20"
                        style={{ background: theme === 'dark' ? 'rgba(35,25,15,0.5)' : 'rgba(255,247,237,0.6)' }}
                      >
                        <div className="text-[10px] opacity-60">{s}</div>
                        <div className="text-sm font-semibold opacity-50">—</div>
                        <div className="text-[10px] opacity-40">loading…</div>
                      </div>
                    ))}
                  </div>

                  {/* Hot Topics strip */}
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold tracking-wider uppercase bg-gradient-to-r from-amber-400 to-orange-600 text-transparent bg-clip-text">
                      Hot Topics
                    </h3>
                    <div className="text-[10px] opacity-50 font-mono">2025</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {[
                      { name: 'ZK Proofs',       hot: true,  accent: '#a78bfa' },
                      { name: 'RWA',             hot: true,  accent: '#10b981' },
                      { name: 'AI × Crypto',     hot: true,  accent: '#06b6d4' },
                      { name: 'Restaking',       hot: false, accent: '#ef4444' },
                      { name: 'BTC L2',          hot: true,  accent: '#f59e0b' },
                      { name: 'Account Abstraction', hot: false, accent: '#6366f1' },
                      { name: 'Modular Chains',  hot: false, accent: '#ec4899' },
                      { name: 'MEV',             hot: false, accent: '#f43f5e' },
                      { name: 'DePIN',           hot: true,  accent: '#0ea5e9' },
                      { name: 'Memecoins',       hot: false, accent: '#fbbf24' },
                    ].map((t, i) => (
                      <span
                        key={t.name}
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-all duration-300 hover:-translate-y-0.5 cursor-default"
                        style={{
                          color: t.accent,
                          background: `${t.accent}14`,
                          border: `1px solid ${t.accent}55`,
                          animation: `slideUp 0.4s ease-out ${i * 0.04}s both`,
                        }}
                      >
                        {t.hot && <span className="text-[8px]">🔥</span>}
                        {t.name}
                      </span>
                    ))}
                  </div>

                  {/* Realistic multi-chain visualization */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-wider opacity-60 flex items-center gap-1">
                      <Layers size={10} /> On-chain Activity · multi-chain
                    </div>
                    <div className="text-[10px] font-mono opacity-50 flex items-center gap-2">
                      <span>BTC <span className="block-counter-btc">872,341</span></span>
                      <span className="opacity-40">|</span>
                      <span>ETH <span className="block-counter-eth">21,082,442</span></span>
                      <span className="opacity-40">|</span>
                      <span>SOL <span className="block-counter-sol">312,847,902</span></span>
                    </div>
                  </div>
                  <div className="relative flex-1 rounded-xl overflow-hidden p-3"
                       style={{
                         background: theme === 'dark'
                           ? 'linear-gradient(135deg, rgba(20,15,10,0.75), rgba(10,8,6,0.55))'
                           : 'linear-gradient(135deg, rgba(255,247,237,0.85), rgba(255,237,213,0.55))',
                         border: '1px solid rgba(245,158,11,0.20)',
                       }}>
                    <svg viewBox="0 0 380 200" className="w-full h-full" style={{ overflow: 'visible' }}>
                      <defs>
                        {/* per-chain block gradients */}
                        <linearGradient id="btcBlock" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f7931a" stopOpacity="0.55" />
                          <stop offset="100%" stopColor="#f7931a" stopOpacity="0.10" />
                        </linearGradient>
                        <linearGradient id="ethBlock" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#627eea" stopOpacity="0.55" />
                          <stop offset="100%" stopColor="#627eea" stopOpacity="0.10" />
                        </linearGradient>
                        <linearGradient id="solBlock" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#14f195" stopOpacity="0.55" />
                          <stop offset="100%" stopColor="#9945ff" stopOpacity="0.20" />
                        </linearGradient>
                        {/* arrow markers */}
                        <marker id="arrowAmber" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                          <path d="M0,0 L6,3 L0,6 z" fill="#f7931a" opacity="0.7" />
                        </marker>
                        <marker id="arrowEth" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                          <path d="M0,0 L6,3 L0,6 z" fill="#627eea" opacity="0.8" />
                        </marker>
                        <marker id="arrowSol" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                          <path d="M0,0 L6,3 L0,6 z" fill="#14f195" opacity="0.8" />
                        </marker>
                      </defs>

                      {(() => {
                        // Three parallel chains. Each chain has 4 sequential blocks
                        // linked by prev_hash. Block widths/positions are identical
                        // across chains so cross-chain bridges line up vertically.
                        const blockW = 56;
                        const blockH = 28;
                        const gap = 18;       // horizontal arrow gap between blocks
                        const xStart = 60;    // first block left edge
                        const blockX = (i) => xStart + i * (blockW + gap);
                        const arrowX1 = (i) => blockX(i) + blockW;
                        const arrowX2 = (i) => blockX(i + 1);

                        const chains = [
                          {
                            id: 'btc',  label: 'BTC',  color: '#f7931a', fill: 'url(#btcBlock)',
                            marker: 'url(#arrowAmber)', y: 18, sub: '~10 min',
                            heights: [872338, 872339, 872340, 872341],
                            pulseLatest: true,
                          },
                          {
                            id: 'eth',  label: 'ETH',  color: '#627eea', fill: 'url(#ethBlock)',
                            marker: 'url(#arrowEth)',   y: 80, sub: '~12 sec',
                            heights: [21082439, 21082440, 21082441, 21082442],
                            pulseLatest: true,
                            zkOnIndex: 2, // mark one block as ZK-rollup-verified
                          },
                          {
                            id: 'sol',  label: 'SOL',  color: '#14f195', fill: 'url(#solBlock)',
                            marker: 'url(#arrowSol)',   y: 142, sub: '~400 ms',
                            heights: [312847899, 312847900, 312847901, 312847902],
                            pulseLatest: true,
                          },
                        ];

                        const elements = [];

                        chains.forEach((c) => {
                          // Chain label and cadence
                          elements.push(
                            <g key={`label-${c.id}`}>
                              <rect x="0" y={c.y - 2} width="50" height={blockH + 4} rx="4"
                                    fill={`${c.color}1a`} stroke={`${c.color}55`} strokeWidth="0.5" />
                              <text x="25" y={c.y + 12} textAnchor="middle" fontSize="10" fontWeight="700" fill={c.color}>
                                {c.label}
                              </text>
                              <text x="25" y={c.y + 24} textAnchor="middle" fontSize="6" fill="currentColor" opacity="0.55">
                                {c.sub}
                              </text>
                            </g>
                          );

                          // Blocks + prev_hash arrows
                          c.heights.forEach((h, i) => {
                            const x = blockX(i);
                            const isLatest = i === c.heights.length - 1;
                            const isZk = c.zkOnIndex === i;

                            // arrow from this block → next (within the chain)
                            if (i < c.heights.length - 1) {
                              const ax1 = arrowX1(i);
                              const ax2 = arrowX2(i);
                              const ay = c.y + blockH / 2;
                              elements.push(
                                <g key={`arr-${c.id}-${i}`}>
                                  <line x1={ax1 + 1} y1={ay} x2={ax2 - 5} y2={ay}
                                        stroke={c.color} strokeOpacity="0.55" strokeWidth="1"
                                        strokeDasharray="3 2" markerEnd={c.marker}>
                                    <animate attributeName="stroke-dashoffset" values="0;-10" dur="1.4s" repeatCount="indefinite" />
                                  </line>
                                  {/* tiny prev_hash label */}
                                  <text x={(ax1 + ax2) / 2} y={ay - 4} textAnchor="middle" fontSize="5"
                                        fill="currentColor" opacity="0.45" style={{ fontFamily: 'ui-monospace, monospace' }}>
                                    prev_hash
                                  </text>
                                </g>
                              );
                            }

                            // The block itself
                            elements.push(
                              <g key={`blk-${c.id}-${i}`} style={{ animation: `slideUp 0.6s ease-out ${i * 0.1}s both` }}>
                                <rect x={x} y={c.y} width={blockW} height={blockH} rx="4"
                                      fill={isZk ? 'url(#ethBlock)' : c.fill}
                                      stroke={isZk ? '#a78bfa' : c.color}
                                      strokeWidth={isLatest ? 1.4 : 0.7}
                                      strokeOpacity={isLatest ? 1 : 0.55}>
                                  {isLatest && c.pulseLatest && (
                                    <animate attributeName="stroke-opacity" values="0.5;1;0.5" dur="1.6s" repeatCount="indefinite" />
                                  )}
                                </rect>
                                {/* block height */}
                                <text x={x + blockW / 2} y={c.y + 11} textAnchor="middle" fontSize="6.5"
                                      fill="currentColor" opacity="0.85" style={{ fontFamily: 'ui-monospace, monospace' }}>
                                  #{h.toLocaleString()}
                                </text>
                                {/* abbreviated block hash */}
                                <text x={x + blockW / 2} y={c.y + 20} textAnchor="middle" fontSize="6"
                                      fill={isZk ? '#a78bfa' : c.color} fontWeight="600"
                                      style={{ fontFamily: 'ui-monospace, monospace' }}>
                                  {isZk ? '0x9a..fb · zk✓' : `0x${(h % 65536).toString(16).padStart(4, '0')}..${((h * 7) % 65536).toString(16).padStart(4, '0')}`}
                                </text>
                              </g>
                            );
                          });
                        });

                        return elements;
                      })()}

                      {/* Cross-chain bridges. Drawn as curved paths between
                          specific blocks across rows, labelled with the actual
                          bridge name. tBTC → BTC↔ETH; Wormhole → ETH↔SOL. */}
                      {(() => {
                        // bridge anchor x position (center of block index 2, the 3rd block).
                        const bridgeX1 = 60 + 2 * (56 + 18) + 56 / 2; // = 208
                        const bridgeX2 = 60 + 0 * (56 + 18) + 56 / 2; // = 88 (for second bridge on left)

                        // BTC bottom y = 18 + 28 = 46; ETH top = 80; ETH bottom = 108; SOL top = 142
                        const btcBottom = 46;
                        const ethTop = 80;
                        const ethBottom = 108;
                        const solTop = 142;

                        return (
                          <g>
                            {/* tBTC: BTC → ETH (lock&mint) */}
                            <path d={`M ${bridgeX1} ${btcBottom} C ${bridgeX1} ${btcBottom + 12}, ${bridgeX1} ${ethTop - 12}, ${bridgeX1} ${ethTop}`}
                                  stroke="#f7931a" strokeWidth="1.2" strokeDasharray="3 2" fill="none" opacity="0.55">
                              <animate attributeName="stroke-dashoffset" values="0;-10" dur="1.4s" repeatCount="indefinite" />
                            </path>
                            <rect x={bridgeX1 - 16} y={(btcBottom + ethTop) / 2 - 6} width="32" height="12" rx="6"
                                  fill="rgba(247,147,26,0.15)" stroke="#f7931a" strokeOpacity="0.6" strokeWidth="0.5" />
                            <text x={bridgeX1} y={(btcBottom + ethTop) / 2 + 2.5} textAnchor="middle"
                                  fontSize="7" fontWeight="700" fill="#f7931a">tBTC</text>
                            {/* particle traveling along bridge */}
                            <circle r="2" fill="#f7931a">
                              <animate attributeName="cy" values={`${btcBottom};${ethTop}`} dur="2.4s" repeatCount="indefinite" />
                              <animate attributeName="cx" values={`${bridgeX1};${bridgeX1}`} dur="2.4s" repeatCount="indefinite" />
                              <animate attributeName="opacity" values="0;1;0" dur="2.4s" repeatCount="indefinite" />
                            </circle>

                            {/* Wormhole: ETH ↔ SOL (lock&mint) */}
                            <path d={`M ${bridgeX2} ${ethBottom} C ${bridgeX2} ${ethBottom + 12}, ${bridgeX2} ${solTop - 12}, ${bridgeX2} ${solTop}`}
                                  stroke="#a78bfa" strokeWidth="1.2" strokeDasharray="3 2" fill="none" opacity="0.65">
                              <animate attributeName="stroke-dashoffset" values="0;-10" dur="1.4s" repeatCount="indefinite" />
                            </path>
                            <rect x={bridgeX2 - 24} y={(ethBottom + solTop) / 2 - 6} width="48" height="12" rx="6"
                                  fill="rgba(167,139,250,0.18)" stroke="#a78bfa" strokeOpacity="0.7" strokeWidth="0.5" />
                            <text x={bridgeX2} y={(ethBottom + solTop) / 2 + 2.5} textAnchor="middle"
                                  fontSize="7" fontWeight="700" fill="#a78bfa">Wormhole</text>
                            <circle r="2" fill="#a78bfa">
                              <animate attributeName="cy" values={`${ethBottom};${solTop}`} dur="2s" repeatCount="indefinite" begin="0.4s" />
                              <animate attributeName="cx" values={`${bridgeX2};${bridgeX2}`} dur="2s" repeatCount="indefinite" begin="0.4s" />
                              <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="0.4s" />
                            </circle>
                            <circle r="2" fill="#a78bfa">
                              <animate attributeName="cy" values={`${solTop};${ethBottom}`} dur="2.6s" repeatCount="indefinite" begin="1.3s" />
                              <animate attributeName="cx" values={`${bridgeX2};${bridgeX2}`} dur="2.6s" repeatCount="indefinite" begin="1.3s" />
                              <animate attributeName="opacity" values="0;1;0" dur="2.6s" repeatCount="indefinite" begin="1.3s" />
                            </circle>
                          </g>
                        );
                      })()}

                      {/* mempool feeding latest ETH block */}
                      {[0, 1, 2, 3].map((i) => (
                        <circle key={`mp-${i}`} cx={60 + 3 * (56 + 18) + 56 / 2 + (i - 1.5) * 4} r="1.4" fill="#627eea">
                          <animate attributeName="cy" values={`${64};${80}`} dur={`${1.4 + (i % 3) * 0.3}s`}
                                   repeatCount="indefinite" begin={`${i * 0.2}s`} />
                          <animate attributeName="opacity" values="0;1;0" dur={`${1.4 + (i % 3) * 0.3}s`}
                                   repeatCount="indefinite" begin={`${i * 0.2}s`} />
                        </circle>
                      ))}

                      {/* Footer: ZK rollup + Cosmos hub note */}
                      <g>
                        <rect x="0" y="183" width="380" height="17" rx="3" fill="rgba(255,255,255,0.03)" />
                        <text x="6" y="194" fontSize="7" fill="#a78bfa" fontWeight="700" style={{ fontFamily: 'ui-monospace, monospace' }}>
                          ZK rollup
                        </text>
                        <text x="58" y="194" fontSize="6.5" fill="currentColor" opacity="0.6" style={{ fontFamily: 'ui-monospace, monospace' }}>
                          ETH block #21,082,441 verified by validity proof
                        </text>
                        <text x="270" y="194" fontSize="6.5" fill="currentColor" opacity="0.55" style={{ fontFamily: 'ui-monospace, monospace' }}>
                          + IBC hub  · Cosmos · Axelar GMP
                        </text>
                      </g>
                    </svg>
                  </div>

                  {/* Bottom: scrolling ticker for all coins */}
                  <div
                    className="relative mt-4 h-9 rounded-full overflow-hidden border border-amber-500/20"
                    style={{ background: theme === 'dark' ? 'rgba(15,15,20,0.55)' : 'rgba(255,255,255,0.6)' }}
                  >
                    <div className="absolute left-0 top-0 bottom-0 z-30 flex items-center gap-1.5 px-3 text-[10px] font-medium text-amber-400 uppercase tracking-wider"
                         style={{
                           background: theme === 'dark'
                             ? 'linear-gradient(to right, rgba(15,15,20,1) 70%, transparent)'
                             : 'linear-gradient(to right, rgba(255,255,255,0.95) 70%, transparent)',
                         }}>
                      <span className="animate-pulse">●</span> Live
                    </div>
                    <div
                      id="crypto-price-ticker"
                      className="absolute inset-0 flex items-center whitespace-nowrap z-10 pl-16"
                      style={{
                        transform: 'translateX(100%)',
                        animation: 'ticker-rtl 35s linear infinite',
                      }}
                    >
                      {/* Filled by useEffect */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className={`py-16 ${theme === 'dark' ? 'bg-gray-800/80' : 'bg-gray-200/80'} backdrop-blur-md relative`}>
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <div className={`absolute inset-0 ${
            theme === 'dark' ? 'bg-gradient-to-t from-gray-900 to-transparent' : 'bg-gradient-to-t from-gray-100 to-transparent'
          }`} />
          
          {/* Grid pattern */}
          <div className="absolute inset-0" 
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(99,102,241,0.1) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(99,102,241,0.1) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 
              className="text-4xl font-bold mb-6 bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-500 text-transparent bg-clip-text"
              style={{
                transform: `translateY(${Math.max(0, 40 - Math.max(0, scrollY - 2700) * 0.1)}px)`,
                opacity: Math.min(1, Math.max(0, scrollY - 2700) / 200),
                transition: 'transform 0.8s ease-out, opacity 0.8s ease-out',
              }}
            >
              Get In Touch
            </h2>
            
            <p 
              className="mb-8 opacity-80"
              style={{
                transform: `translateY(${Math.max(0, 60 - Math.max(0, scrollY - 2700) * 0.1)}px)`,
                opacity: Math.min(1, Math.max(0, scrollY - 2700) / 300),
                transition: 'transform 1s ease-out, opacity 1s ease-out',
              }}
            >
              Interested in connecting? Reach out to discuss impact investing, crypto projects, AI innovations, or a Bazi reading.
            </p>
            
            <div 
              className="flex justify-center gap-6"
              style={{
                transform: `translateY(${Math.max(0, 80 - Math.max(0, scrollY - 2700) * 0.1)}px)`,
                opacity: Math.min(1, Math.max(0, scrollY - 2700) / 400),
                transition: 'transform 1.2s ease-out, opacity 1.2s ease-out',
              }}
            >
              <a 
                href="http://linkedin.com/in/josephl1/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-3 rounded-full border border-transparent hover:border-indigo-500/50 transition-colors"
                onMouseEnter={() => setMouseEntered(true)}
                onMouseLeave={() => setMouseEntered(false)}
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://x.com/bazi_master"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X / Twitter"
                className="p-3 rounded-full border border-transparent hover:border-indigo-500/50 transition-colors"
                onMouseEnter={() => setMouseEntered(true)}
                onMouseLeave={() => setMouseEntered(false)}
              >
                <Twitter size={20} />
              </a>
              <a
                href="https://github.com/joejoe168168/impact-vision"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub – Impact Vision"
                className="p-3 rounded-full border border-transparent hover:border-indigo-500/50 transition-colors"
                onMouseEnter={() => setMouseEntered(true)}
                onMouseLeave={() => setMouseEntered(false)}
              >
                <Github size={20} />
              </a>
            </div>
            
            <div 
              className="text-sm opacity-70 pt-8 border-t border-gray-700/30"
              style={{
                transform: `translateY(${Math.max(0, 100 - Math.max(0, scrollY - 2700) * 0.1)}px)`,
                opacity: Math.min(1, Math.max(0, scrollY - 2700) / 500),
                transition: 'transform 1.4s ease-out, opacity 1.4s ease-out',
              }}
            >
              © {new Date().getFullYear()} Joseph Leung. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {/* Add global animations */}
      <style jsx global>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); opacity: 0.6; }
        }
        
        @keyframes float {
          0% { transform: translateY(0) scale(1); }
          100% { transform: translateY(-10px) scale(1.05); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes growWidth {
          from { width: 0%; }
          to { width: 100%; }
        }
        
        @keyframes pulseOpacity {
          0% { opacity: 0.1; }
          100% { opacity: 0.5; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes grow-stem {
          from { height: 0; }
          to { height: 100%; }
        }
        
        @keyframes grow-leaf {
          from { transform: scale(0) rotate(var(--rotation, 0deg)); }
          to { transform: scale(1) rotate(var(--rotation, 0deg)); }
        }
        
        @keyframes bloom {
          0% { transform: scale(0); }
          70% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        @keyframes orbit {
          from { transform: rotate(0deg) translateX(0) rotate(0deg); }
          to { transform: rotate(360deg) translateX(0) rotate(-360deg); }
        }

        @keyframes dataPulse {
          0% { width: 0; opacity: 0.9; }
          100% { width: 150px; opacity: 0.2; }
        }

        @keyframes pulse-subtle {
          0% { opacity: 0.1; }
          50% { opacity: 0.2; }
          100% { opacity: 0.1; }
        }

        @keyframes dash {
          to {
            stroke-dashoffset: 24;
          }
        }

        @keyframes cryptoParticle {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translate(-100px, -20px) scale(0);
            opacity: 0;
          }
        }

        @keyframes rotateY {
          0% { transform: rotateY(0deg); }
          50% { transform: rotateY(180deg); }
          100% { transform: rotateY(360deg); }
        }

        .animate-rotate-y {
          animation: rotateY 6s infinite linear;
          transform-style: preserve-3d;
        }

        .perspective-500 {
          perspective: 500px;
        }

        .transform-style-3d {
          transform-style: preserve-3d;
        }

        .rotate-y-180 {
          transform: rotateY(180deg);
        }

        @keyframes defiParticle {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translate(300px, 0px) scale(0);
            opacity: 0;
          }
        }

        @keyframes spinTaiji {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }s

        @keyframes spin-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .animate-spin-slow {
          animation: spin-slow 60s linear infinite;
        }

        .marquee-container {
          animation: marquee 8s linear infinite;
        }

        @keyframes marquee {
          0% { transform: translateX(0); }
          45% { transform: translateX(0); }
          50% { transform: translateX(-120px); }
          95% { transform: translateX(-120px); }
          100% { transform: translateX(0); }
        }

        @keyframes defiParticleFixed {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translate(250px, 0px) scale(0);
            opacity: 0;
          }
        }

        @keyframes marquee-smooth {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); } /* Translate by half the total width */
        }

        .animate-marquee-smooth {
          animation: marquee-smooth 12s linear infinite; /* Adjust duration as needed */
        }

        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .animate-ticker {
          animation: ticker 20s linear infinite;
        }

        .animate-spin-slower {
          animation: spin 15s linear infinite;
        }

        @keyframes floatKeywords {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 0; }
          10% { opacity: 0.15; }
          90% { opacity: 0.15; }
          100% { transform: translate(${Math.random() > 0.5 ? '' : '-'}${10 + Math.random() * 20}px, ${Math.random() > 0.5 ? '' : '-'}${10 + Math.random() * 20}px) rotate(${Math.random() > 0.5 ? '' : '-'}${1 + Math.random() * 3}deg); opacity: 0; }
        }

        .keywords-container {
          position: absolute;
          width: 100%;
          height: 100%;
          transform: translateZ(0);
        }

        .keyword-item {
          position: absolute;
          font-size: 2rem;
          font-weight: bold;
          white-space: nowrap;
          will-change: transform, opacity;
          transition: transform 0.3s ease-out;
          animation: pulseKeywords 8s infinite ease-in-out;
          z-index: 1;
          text-shadow: 0 0 15px rgba(99, 102, 241, 0.6);
        }

        @keyframes pulseKeywords {
          0% { opacity: 0; }
          30% { opacity: 0.2 + (Math.random() * 0.3); }
          70% { opacity: 0.2 + (Math.random() * 0.3); }
          100% { opacity: 0; }
        }

        @media (max-width: 768px) {
          .keyword-item {
            font-size: 1.5rem;
          }
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        @keyframes glow {
          0% { box-shadow: 0 0 5px rgba(251, 191, 36, 0.3); }
          100% { box-shadow: 0 0 20px rgba(251, 191, 36, 0.6); }
        }

        @keyframes floatRandom1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          20% { transform: translate(50px, 20px) scale(1.05); }
          40% { transform: translate(20px, 70px) scale(0.95); }
          60% { transform: translate(-30px, 40px) scale(1.02); }
          80% { transform: translate(-60px, -30px) scale(0.98); }
        }

        @keyframes floatRandom2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-40px, -50px) scale(1.03); }
          45% { transform: translate(30px, -30px) scale(0.97); }
          65% { transform: translate(60px, 20px) scale(1.01); }
          85% { transform: translate(10px, 60px) scale(0.99); }
        }

        @keyframes floatRandom3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          15% { transform: translate(-20px, 40px) scale(1.04); }
          35% { transform: translate(-60px, -10px) scale(0.96); }
          55% { transform: translate(10px, -50px) scale(1.02); }
          75% { transform: translate(50px, 10px) scale(0.98); }
        }

        @keyframes floatRandom4 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          20% { transform: translate(30px, -60px) scale(1.01); }
          40% { transform: translate(50px, 0px) scale(0.97); }
          60% { transform: translate(10px, 50px) scale(1.03); }
          80% { transform: translate(-40px, 20px) scale(0.99); }
        }

        @keyframes floatRandom5 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-50px, 10px) scale(1.02); }
          45% { transform: translate(-10px, -40px) scale(0.98); }
          65% { transform: translate(40px, -10px) scale(1.04); }
          85% { transform: translate(20px, 30px) scale(0.96); }
        }

        @keyframes floatRandom6 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          15% { transform: translate(20px, 50px) scale(0.97); }
          35% { transform: translate(-30px, 30px) scale(1.03); }
          55% { transform: translate(-50px, -20px) scale(0.99); }
          75% { transform: translate(10px, -40px) scale(1.02); }
        }

        @keyframes floatRandom7 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          20% { transform: translate(-20px, -30px) scale(1.01); }
          40% { transform: translate(40px, -50px) scale(0.97); }
          60% { transform: translate(50px, 30px) scale(1.03); }
          80% { transform: translate(-40px, 60px) scale(0.99); }
        }

        @keyframes floatRandom8 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(60px, -10px) scale(0.98); }
          45% { transform: translate(20px, 40px) scale(1.02); }
          65% { transform: translate(-30px, 60px) scale(0.96); }
          85% { transform: translate(-60px, 0px) scale(1.04); }
        }

        @keyframes floatRandom9 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          15% { transform: translate(30px, 60px) scale(1.03); }
          35% { transform: translate(-40px, 20px) scale(0.97); }
          55% { transform: translate(-10px, -30px) scale(1.01); }
          75% { transform: translate(50px, -40px) scale(0.99); }
        }

        @keyframes ticker-rtl {
          0% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
        .animate-ticker-rtl {
          animation: ticker-rtl 25s linear infinite;
        }

        @keyframes blobDrift {
          0%   { transform: translate(0, 0) scale(1); }
          50%  { transform: translate(40px, -30px) scale(1.08); }
          100% { transform: translate(-30px, 40px) scale(0.95); }
        }

        @keyframes twinkle {
          0%   { opacity: 0.2; transform: scale(0.8); }
          100% { opacity: 1;   transform: scale(1.4); }
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulseSoft {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.35); }
          50%      { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
        }

        @keyframes pulseNode {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50%      { transform: scale(1.18); opacity: 1; }
        }
        circle[style*="pulseNode"] { transform-box: fill-box; transform-origin: center; }

        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .anim-spin-slow { animation: spinSlow 40s linear infinite; transform-origin: center; transform-box: fill-box; }
        .anim-spin-slow-rev { animation: spinSlow 60s linear infinite reverse; transform-origin: center; transform-box: fill-box; }

        @keyframes taijiBreath {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 8px rgba(168,85,247,0.45)); }
          50%      { transform: scale(1.04); filter: drop-shadow(0 0 22px rgba(168,85,247,0.85)); }
        }

        /* Respect users who request less motion: kill all loops, keep transitions short */
        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </div>
  );
};

export default App;